import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Sparkles, Package, Loader2, Search, X, RefreshCw, Trash2, Wand2,
  CheckCircle2, XCircle, Image as ImageIcon, ImageOff, Eye, ClipboardCopy,
  Link2, Link2Off, CalendarDays, LayoutGrid, PenSquare, Upload,
  Send, MessageSquareWarning, Clock, ShieldCheck, Copy as CopyIcon,
  UserCheck, Users, ListChecks, Activity, History as HistoryIcon,
  User as UserIcon, ChevronUp, ChevronDown, ChevronsUpDown,
  Download, FileText, Type, Hash, Bot, RefreshCcw, Star,
} from "lucide-react";
import { fmtRelative } from "@/lib/format";
import { TypeIcon, TypeChip, colorForFormat } from "@/lib/formatIcon";

const STATUS_TONE = {
  queued: "border-sky-400 bg-sky-50 text-sky-800",
  draft: "border-amber-400 bg-amber-50 text-amber-800",
  internal_review: "border-violet-500 bg-violet-50 text-violet-800",
  internal_approved: "border-teal-500 bg-teal-50 text-teal-800",
  client_review: "border-fuchsia-500 bg-fuchsia-50 text-fuchsia-800",
  changes_requested: "border-orange-500 bg-orange-50 text-orange-800",
  approved: "border-emerald-500 bg-emerald-50 text-emerald-800",
  linked: "border-emerald-600 bg-emerald-100 text-emerald-900",
  rejected: "border-rose-500 bg-rose-50 text-rose-800",
  error: "border-rose-500 bg-rose-50 text-rose-800",
  archived: "border-border bg-muted/40 text-muted-foreground",
};

const KPI_STATUSES = ["draft", "internal_review", "client_review",
                                "changes_requested", "approved", "rejected"];

// SLA color helper — returns {label, cls}
function slaChip(sla_due_at) {
  if (!sla_due_at) return null;
  const due = new Date(sla_due_at).getTime();
  if (isNaN(due)) return null;
  const now = Date.now();
  const diffH = (due - now) / 3600000;
  if (diffH < 0) {
    const hoursOver = Math.abs(Math.round(diffH));
    return { label: `SLA breached · ${hoursOver}h over`, cls: "border-rose-500 bg-rose-100 text-rose-900 font-semibold" };
  }
  if (diffH < 6)  return { label: `SLA ${Math.round(diffH)}h`, cls: "border-rose-400 bg-rose-50 text-rose-800" };
  if (diffH < 24) return { label: `SLA ${Math.round(diffH)}h`, cls: "border-amber-400 bg-amber-50 text-amber-800" };
  const days = Math.round(diffH / 24);
  return { label: `SLA ${days}d`, cls: "border-emerald-400 bg-emerald-50 text-emerald-800" };
}

// Human-friendly duration formatter for cycle-time / stage-time chips.
function fmtDuration(seconds) {
  if (seconds == null || isNaN(seconds)) return null;
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`.replace(" 0m", "");
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`.replace(" 0h", "");
}

export default function BrandContentBucketTab({ brand }) {
  const wsId = brand?.workspace_id;
  const bid = brand?.id;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formats, setFormats] = useState([]);
  const [strategy, setStrategy] = useState(null);
  const [q, setQ] = useState("");
  const [fFormat, setFFormat] = useState("_all");
  const [fStatus, setFStatus] = useState("_all");
  const [genOpen, setGenOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [view, setView] = useState("grid");  // grid | timeline | list
  const [manualOpen, setManualOpen] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);
  const [selected, setSelected] = useState(() => new Set()); // bulk-selected item IDs
  const [bulkBusy, setBulkBusy] = useState(false);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc"); // asc | desc
  const [showPreviews, setShowPreviews] = useState(false);   // Iter 186 · preview toggle
  const [leaderboardOpen, setLeaderboardOpen] = useState(false); // Iter 188

  const loadAll = async () => {
    if (!wsId || !bid) return;
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get(`/workspaces/${wsId}/brands/${bid}/content-bucket`, { params: { format: fFormat, status: fStatus, q, with_previews: showPreviews ? 1 : 0 } }),
        api.get(`/content-bucket/formats`),
        api.get(`/workspaces/${wsId}/brands/${bid}/content-strategy/latest`),
      ]);
      setRows(r1.data.rows || []);
      setFormats(r2.data.formats || []);
      setStrategy(r3.data || null);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, [wsId, bid, fFormat, fStatus, showPreviews]);

  // Live poll while any item is still generating
  useEffect(() => {
    const hasQueued = rows.some((r) => r.generating);
    if (!hasQueued) return;
    const t = setInterval(loadAll, 5000);
    return () => clearInterval(t);
  }, [rows]);

  const byStatus = useMemo(() => {
    const c = { draft: 0, internal_review: 0, client_review: 0,
                    changes_requested: 0, approved: 0, rejected: 0,
                    queued: 0, error: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const slaCounts = useMemo(() => {
    let due = 0, breached = 0;
    rows.forEach((r) => {
      if (!r.sla_due_at) return;
      const d = new Date(r.sla_due_at).getTime();
      if (isNaN(d)) return;
      if (["approved", "rejected", "archived"].includes(r.status)) return;
      const diffH = (d - Date.now()) / 3600000;
      if (diffH < 0) breached += 1;
      else if (diffH < 24) due += 1;
    });
    return { due, breached };
  }, [rows]);

  // Sorted rows for list view — sortable by any of the column keys.
  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const getVal = (r) => {
      switch (sortKey) {
        case "topic": return (r.topic_title || "").toLowerCase();
        case "format": return r.format || "";
        case "status": return r.status || "";
        case "sla": return r.sla_due_at || "";
        case "author": return (r.created_by_name || "").toLowerCase();
        case "last_edited": return r.last_edited_at || r.updated_at || "";
        case "cycle": return r.timings?.total_cycle_seconds ?? 0;
        case "version": return r.version || 1;
        case "created_at":
        default: return r.created_at || "";
      }
    };
    arr.sort((a, b) => {
      const av = getVal(a), bv = getVal(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  // Bulk action runner
  const runBulk = async (action) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brands/${bid}/content-bucket/bulk`,
        { action, ids: Array.from(selected) });
      const { ok_count, count, results } = r.data;
      const failed = results.filter((x) => !x.ok);
      if (failed.length > 0) {
        toast.warning(`${ok_count}/${count} succeeded · ${failed.length} skipped`);
      } else {
        toast.success(`${ok_count} item(s) updated`);
      }
      setSelected(new Set());
      loadAll();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk action failed")); }
    finally { setBulkBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="brand-content-bucket-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-primary" />
          <h2 className="text-sm font-semibold">Content Bucket</h2>
          <Badge variant="outline" className="rounded-none text-[10px]">{rows.length} items</Badge>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex border border-border" data-testid="content-bucket-view-toggle">
            <button type="button" onClick={() => setView("grid")}
                      className={`h-8 px-2 text-xs inline-flex items-center gap-1 ${view === "grid" ? "bg-primary text-primary-foreground" : "bg-card"}`}
                      data-testid="content-bucket-view-grid">
              <LayoutGrid className="w-3.5 h-3.5" /> Grid
            </button>
            <button type="button" onClick={() => setView("list")}
                      className={`h-8 px-2 text-xs inline-flex items-center gap-1 border-l border-border ${view === "list" ? "bg-primary text-primary-foreground" : "bg-card"}`}
                      data-testid="content-bucket-view-list">
              <ListChecks className="w-3.5 h-3.5" /> List
            </button>
            <button type="button" onClick={() => setView("timeline")}
                      className={`h-8 px-2 text-xs inline-flex items-center gap-1 border-l border-border ${view === "timeline" ? "bg-primary text-primary-foreground" : "bg-card"}`}
                      data-testid="content-bucket-view-timeline">
              <CalendarDays className="w-3.5 h-3.5" /> Timeline
            </button>
          </div>
          <Button variant="outline" size="sm" className="rounded-none h-8"
                    onClick={() => setLeaderboardOpen(true)}
                    data-testid="content-bucket-leaderboard-btn">
            <Star className="w-3.5 h-3.5 mr-1.5" /> Leaderboard
          </Button>
          <Button variant="outline" size="sm" className="rounded-none h-8"
                    onClick={() => setShowPreviews(!showPreviews)}
                    data-testid="content-bucket-preview-toggle">
            {showPreviews ? <ImageOff className="w-3.5 h-3.5 mr-1.5" /> : <ImageIcon className="w-3.5 h-3.5 mr-1.5" />}
            {showPreviews ? "Hide previews" : "Show previews"}
          </Button>
          <Button variant="outline" size="sm" className="rounded-none h-8"
                    onClick={() => setManualOpen(true)}
                    data-testid="content-bucket-manual-btn">
            <PenSquare className="w-3.5 h-3.5 mr-1.5" /> Add manual item
          </Button>
          <Button size="sm" className="rounded-none h-8" onClick={() => setGenOpen(true)}
                    disabled={!strategy || !strategy.proposed_topics?.length}
                    data-testid="content-bucket-generate-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generate content
          </Button>
        </div>
      </div>

      {!strategy || !(strategy.proposed_topics || []).length ? (
        <div className="border border-dashed border-border p-6 text-center bg-amber-50/40" data-testid="content-bucket-no-strategy">
          <Sparkles className="w-6 h-6 mx-auto text-amber-600 mb-1" />
          <div className="text-sm font-medium">No approved strategy yet</div>
          <div className="text-xs text-muted-foreground mt-1">
            AI generation needs a Content Strategy first. You can still <strong>add manual items</strong> below.
          </div>
        </div>
      ) : null}
      {view === "timeline" ? (
        <TimelineView wsId={wsId} bid={bid} />
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {KPI_STATUSES.map((s) => (
              <div key={s} className="border border-border p-2 bg-card" data-testid={`content-bucket-kpi-${s}`}>
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">{s.replace(/_/g, " ")}</div>
                <div className="text-xl font-mono font-black mt-0.5">{byStatus[s] || 0}</div>
              </div>
            ))}
          </div>
          {(slaCounts.due > 0 || slaCounts.breached > 0) && (
            <div className="grid grid-cols-2 gap-2" data-testid="content-bucket-sla-strip">
              <div className={`border p-2 ${slaCounts.breached > 0 ? "border-rose-500 bg-rose-50" : "border-border bg-card"}`}
                     data-testid="content-bucket-sla-breached">
                <div className="text-[9px] uppercase tracking-widest text-rose-800 font-bold flex items-center gap-1">
                  <MessageSquareWarning className="w-3 h-3" /> SLA breached
                </div>
                <div className="text-xl font-mono font-black mt-0.5 text-rose-900">{slaCounts.breached}</div>
              </div>
              <div className={`border p-2 ${slaCounts.due > 0 ? "border-amber-500 bg-amber-50" : "border-border bg-card"}`}
                     data-testid="content-bucket-sla-due">
                <div className="text-[9px] uppercase tracking-widest text-amber-800 font-bold flex items-center gap-1">
                  <Clock className="w-3 h-3" /> SLA due within 24h
                </div>
                <div className="text-xl font-mono font-black mt-0.5 text-amber-900">{slaCounts.due}</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap border border-border bg-card p-2">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
              <Input className="pl-9 h-8 rounded-none text-xs" placeholder="Search topic…"
                       value={q} onChange={(e) => setQ(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter") loadAll(); }}
                       data-testid="content-bucket-search" />
            </div>
            <Select value={fFormat} onValueChange={setFFormat}>
              <SelectTrigger className="h-8 w-44 rounded-none text-xs" data-testid="content-bucket-format-filter"><SelectValue placeholder="Format" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="_all">All formats</SelectItem>
                {formats.map((f) => <SelectItem key={f.key} value={f.key}>{f.key}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fStatus} onValueChange={setFStatus}>
              <SelectTrigger className="h-8 w-40 rounded-none text-xs" data-testid="content-bucket-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Any status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="internal_review">Internal review</SelectItem>
                <SelectItem value="client_review">Client review</SelectItem>
                <SelectItem value="changes_requested">Changes requested</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={loadAll}
                      data-testid="content-bucket-refresh-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
          </div>

          {/* Bulk action bar — appears when items selected */}
          {selected.size > 0 && (
            <div className="border border-primary bg-primary/5 p-2 flex items-center gap-2 flex-wrap"
                   data-testid="content-bucket-bulk-bar">
              <span className="text-xs font-semibold text-primary">
                {selected.size} selected
              </span>
              <span className="ml-auto flex items-center gap-1 flex-wrap">
                <Button variant="outline" size="sm" className="rounded-none h-7 text-xs"
                          onClick={() => runBulk("submit-review")} disabled={bulkBusy}
                          data-testid="content-bucket-bulk-submit-btn">
                  <Send className="w-3 h-3 mr-1" /> Submit for review
                </Button>
                <Button variant="outline" size="sm" className="rounded-none h-7 text-xs"
                          onClick={() => runBulk("approve-internal")} disabled={bulkBusy}
                          data-testid="content-bucket-bulk-approve-btn">
                  <UserCheck className="w-3 h-3 mr-1" /> Approve internal
                </Button>
                <Button variant="outline" size="sm" className="rounded-none h-7 text-xs"
                          onClick={() => runBulk("archive")} disabled={bulkBusy}
                          data-testid="content-bucket-bulk-archive-btn">
                  Archive
                </Button>
                <Button variant="outline" size="sm" className="rounded-none h-7 text-xs border-rose-400 text-rose-700 hover:bg-rose-50"
                          onClick={() => {
                            if (window.confirm(`Delete ${selected.size} item(s)? This cannot be undone.`)) {
                              runBulk("delete");
                            }
                          }} disabled={bulkBusy}
                          data-testid="content-bucket-bulk-delete-btn">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
                <Button variant="ghost" size="sm" className="rounded-none h-7 text-xs"
                          onClick={() => setSelected(new Set())}
                          data-testid="content-bucket-bulk-clear-btn">
                  Clear
                </Button>
              </span>
            </div>
          )}

          {/* Grid or List */}
          {loading ? (
            <div className="text-sm text-muted-foreground p-8 text-center">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground italic">
              No content generated yet. Click <strong>Generate content</strong> to queue a batch.
            </div>
          ) : view === "list" ? (
            <ListView rows={sortedRows} selected={selected}
                          onToggle={(id) => {
                            const s = new Set(selected);
                            if (s.has(id)) s.delete(id); else s.add(id);
                            setSelected(s);
                          }}
                          onToggleAll={(all) => {
                            if (all) setSelected(new Set(sortedRows.map((r) => r.id)));
                            else setSelected(new Set());
                          }}
                          onOpen={setDetail}
                          sortKey={sortKey} sortDir={sortDir}
                          onSort={(k) => {
                            if (k === sortKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
                            else { setSortKey(k); setSortDir("desc"); }
                          }} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="content-bucket-grid">
              {rows.map((r) => (
                <BucketCard key={r.id} row={r} onOpen={() => setDetail(r)} />
              ))}
            </div>
          )}
        </>
      )}

      {genOpen && (
        <GenerateDialog wsId={wsId} bid={bid} strategy={strategy} formats={formats}
                          onClose={() => setGenOpen(false)}
                          onQueued={() => { setGenOpen(false); loadAll(); }} />
      )}
      {detail && (
        <BucketDetailDialog wsId={wsId} bid={bid} initial={detail}
                                  onClose={() => { setDetail(null); loadAll(); }}
                                  onRequestLink={(row) => { setDetail(null); setLinkTarget(row); }} />
      )}
      {manualOpen && (
        <ManualCreateDialog wsId={wsId} bid={bid} formats={formats}
                                onClose={() => setManualOpen(false)}
                                onCreated={() => { setManualOpen(false); loadAll(); }} />
      )}
      {leaderboardOpen && (
        <LeaderboardDialog wsId={wsId} bid={bid}
                                   onClose={() => setLeaderboardOpen(false)} />
      )}
      {linkTarget && (
        <LinkCalendarDialog wsId={wsId} bid={bid} bucket={linkTarget}
                                 onClose={() => setLinkTarget(null)}
                                 onLinked={() => { setLinkTarget(null); loadAll(); }} />
      )}
    </div>
  );
}

function BucketCard({ row, onOpen }) {
  const tone = STATUS_TONE[row.status] || STATUS_TONE.draft;
  const title = row.content?.title || row.content?.body?.slice(0, 60) || row.topic_title;
  const sla = slaChip(row.sla_due_at);
  const ci = row.linked_content_item;
  return (
    <button type="button" onClick={onOpen}
              className="border border-border bg-card p-3 text-left hover:shadow-md hover:border-primary transition-all focus:outline-none focus:ring-2 focus:ring-primary flex flex-col"
              data-testid={`content-bucket-card-${row.id}`}>
      {row.preview_uri && (
        <img src={row.preview_uri} alt=""
                className="w-full aspect-square object-cover border border-border mb-2"
                data-testid={`content-bucket-card-preview-${row.id}`} />
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1 min-w-0">
          <TypeIcon format={row.format} size={13} />
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-mono truncate">{row.format}</div>
        </div>
        <span className={`text-[9px] uppercase font-bold border px-1 py-0.5 ${tone}`} data-testid={`content-bucket-card-status-${row.id}`}>{(row.status || "").replace(/_/g, " ")}</span>
      </div>
      <div className="mt-1 text-sm font-semibold line-clamp-2">{title}</div>
      <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{row.topic_title}</div>
      <div className="mt-2 flex items-center gap-1 flex-wrap">
        {row.generating && (
          <span className="inline-flex items-center gap-1 text-[10px] text-primary">
            <Loader2 className="w-3 h-3 animate-spin" /> generating…
          </span>
        )}
        {sla && (
          <span className={`inline-flex items-center gap-0.5 text-[10px] border px-1 ${sla.cls}`}
                  data-testid={`content-bucket-card-sla-${row.id}`}>
            <Clock className="w-2.5 h-2.5" /> {sla.label}
          </span>
        )}
        {row.client_visible && (
          <span className="inline-flex items-center gap-0.5 text-[10px] border border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800 px-1"
                  title="Client visible"
                  data-testid={`content-bucket-card-client-${row.id}`}>
            <Users className="w-2.5 h-2.5" /> client
          </span>
        )}
        {row.ab_variant_tone && (
          <span className="inline-flex items-center gap-0.5 text-[10px] border border-indigo-300 bg-indigo-50 text-indigo-800 px-1"
                  title="A/B tone variant">
            A/B · {row.ab_variant_tone}
          </span>
        )}
        {row.image_count > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
            <ImageIcon className="w-3 h-3" /> {row.image_count}
          </span>
        )}
        {ci && (
          <span className="inline-flex items-center gap-0.5 text-[10px] px-1 max-w-full truncate"
                  style={{
                    color: ci.activity_type_color || "#059669",
                    borderColor: (ci.activity_type_color || "#059669") + "66",
                    borderWidth: 1,
                    borderStyle: "solid",
                    background: (ci.activity_type_color || "#059669") + "10",
                  }}
                  title={`Deliverable: ${ci.activity_type_name || ci.name || "—"} · ${ci.planned_date || ""}${ci.package_name ? ` · ${ci.package_name}` : ""}`}
                  data-testid={`content-bucket-card-deliverable-${row.id}`}>
            <TypeIcon iconKey={ci.activity_type_icon} format={row.format}
                             color={ci.activity_type_color} size={10} />
            <CalendarDays className="w-2.5 h-2.5" />
            <span className="truncate">{ci.activity_type_name || ci.name || "Calendar"}</span>
            {ci.planned_date && <span className="opacity-70">· {ci.planned_date.slice(5)}</span>}
          </span>
        )}
        {row.generation_source === "manual" && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-violet-700 border border-violet-300 bg-violet-50 px-1" title="Human-authored">
            <PenSquare className="w-2.5 h-2.5" /> manual
          </span>
        )}
        {row.rating_avg != null && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-700"
                  title={`${row.rating_avg} avg · ${row.rating_count} rating${row.rating_count === 1 ? "" : "s"}`}
                  data-testid={`content-bucket-card-rating-${row.id}`}>
            <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
            <span className="font-mono">{row.rating_avg.toFixed(1)}</span>
            <span className="opacity-60">({row.rating_count})</span>
          </span>
        )}
        {row.version > 1 && <Badge variant="outline" className="rounded-none text-[9px]">v{row.version}</Badge>}
        {row.error && <span className="text-[10px] text-rose-700 truncate flex-1" title={row.error}>{row.error.slice(0, 40)}</span>}
        <span className="ml-auto text-[10px] text-muted-foreground">{fmtRelative(row.created_at)}</span>
      </div>
    </button>
  );
}

function GenerateDialog({ wsId, bid, strategy, formats, onClose, onQueued }) {
  const [selTopics, setSelTopics] = useState(new Set());
  const [selFormats, setSelFormats] = useState(new Set());
  const [instructions, setInstructions] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const topics = strategy.proposed_topics || [];
  const filteredTopics = q ? topics.filter((t) => (t.title || "").toLowerCase().includes(q.toLowerCase())) : topics;
  const toggleTopic = (title) => {
    const s = new Set(selTopics);
    if (s.has(title)) s.delete(title); else s.add(title);
    setSelTopics(s);
  };
  const toggleFormat = (f) => {
    const s = new Set(selFormats);
    if (s.has(f)) s.delete(f); else s.add(f);
    setSelFormats(s);
  };

  const totalCombos = selTopics.size * selFormats.size;
  const canSubmit = totalCombos > 0 && totalCombos <= 100;

  const submit = async () => {
    setBusy(true);
    try {
      const chosenTopics = topics.filter((t) => selTopics.has(t.title));
      const r = await api.post(`/workspaces/${wsId}/brands/${bid}/content-bucket/generate`, {
        topics: chosenTopics,
        formats: Array.from(selFormats),
        strategy_id: strategy.id,
        custom_instructions: instructions || null,
      });
      toast.success(`Queued ${r.data.count} generation${r.data.count === 1 ? "" : "s"} — grid updates as they finish`);
      onQueued();
    } catch (e) { toast.error(apiErrorMessage(e, "Queue failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="content-bucket-generate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" /> Queue content generation
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[520px] overflow-hidden">
          {/* Topics */}
          <div className="border border-border">
            <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border bg-muted/30">
              <span className="text-[10px] uppercase tracking-widest font-semibold">Topics ({selTopics.size}/{topics.length})</span>
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="Filter…" className="h-6 rounded-none ml-auto w-32 text-xs" />
            </div>
            <div className="max-h-[420px] overflow-auto">
              {filteredTopics.map((t, i) => {
                const on = selTopics.has(t.title);
                return (
                  <button key={i} type="button" onClick={() => toggleTopic(t.title)}
                            className={`w-full text-left px-2 py-1.5 border-b border-border hover:bg-muted/30 ${on ? "bg-primary/10" : ""}`}
                            data-testid={`content-bucket-topic-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-3 h-3 border ${on ? "bg-primary border-primary" : "border-border"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">{t.title}</div>
                        <div className="text-[10px] text-muted-foreground truncate">{t.pillar} · {t.funnel_stage}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Formats + instructions */}
          <div className="flex flex-col gap-2">
            <div className="border border-border">
              <div className="px-2 py-1.5 border-b border-border bg-muted/30 text-[10px] uppercase tracking-widest font-semibold">
                Formats ({selFormats.size}/{formats.length})
              </div>
              <div className="max-h-[240px] overflow-auto p-2 grid grid-cols-1 gap-0.5">
                {formats.map((f) => {
                  const on = selFormats.has(f.key);
                  return (
                    <button key={f.key} type="button" onClick={() => toggleFormat(f.key)}
                              className={`flex items-center gap-2 px-2 py-1 text-left hover:bg-muted/30 ${on ? "bg-primary/10" : ""}`}
                              data-testid={`content-bucket-format-${f.key}`}>
                      <span className={`w-3 h-3 border ${on ? "bg-primary border-primary" : "border-border"}`} />
                      <span className="text-xs font-mono flex-1">{f.key}</span>
                      {f.needs_image && <span className="text-[9px] text-muted-foreground inline-flex items-center gap-0.5"><ImageIcon className="w-2.5 h-2.5" /> {f.images_n}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Custom instructions</Label>
              <Textarea rows={4} value={instructions}
                          onChange={(e) => setInstructions(e.target.value)}
                          placeholder="Apply to ALL selected items. e.g. 'Use a punchy, casual tone. Cite the new pricing tier.'"
                          className="rounded-none" data-testid="content-bucket-instructions" />
            </div>
            <div className="border border-border bg-muted/20 p-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Will queue</div>
              <div className="text-xl font-mono font-black">{totalCombos} <span className="text-xs font-normal">generation(s)</span></div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {selTopics.size} topic(s) × {selFormats.size} format(s). Max 100 per batch.
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={!canSubmit || busy}
                    data-testid="content-bucket-queue-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Queue {totalCombos > 0 ? totalCombos : ""} generation{totalCombos === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BucketDetailDialog({ wsId, bid, initial, onClose, onRequestLink }) {
  const [row, setRow] = useState(initial);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenText, setRegenText] = useState("");
  const [busy, setBusy] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState("");
  const [friendlyEdit, setFriendlyEdit] = useState(null);   // Iter 186 — friendly editor draft
  const [advancedJson, setAdvancedJson] = useState(false);  // toggle JSON fallback
  const [submitOpen, setSubmitOpen] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [changesNote, setChangesNote] = useState("");
  const [slaOpen, setSlaOpen] = useState(false);
  const [abOpen, setAbOpen] = useState(false);
  const uploadInputRef = useRef(null);        // Iter 186 — hidden file input for uploads

  const reload = async () => {
    try {
      const r = await api.get(`/workspaces/${wsId}/brands/${bid}/content-bucket/${initial.id}`);
      setRow(r.data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { reload(); }, [initial.id]);

  // Poll while generating
  useEffect(() => {
    if (!row?.generating) return;
    const t = setInterval(reload, 4000);
    return () => clearInterval(t);
  }, [row?.generating]);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const r = await api.patch(`/workspaces/${wsId}/brands/${bid}/content-bucket/${row.id}`, { status });
      setRow(r.data);
      toast.success(`Status → ${status}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    try {
      const content = JSON.parse(editText);
      const r = await api.patch(`/workspaces/${wsId}/brands/${bid}/content-bucket/${row.id}`, { content });
      setRow(r.data);
      setEditMode(false);
      setAdvancedJson(false);
      toast.success("Saved");
    } catch (e) {
      if (e instanceof SyntaxError) toast.error("Invalid JSON");
      else toast.error(apiErrorMessage(e, "Save failed"));
    }
  };

  // Iter 186 · Slice A — friendly editor save
  const saveFriendly = async () => {
    setBusy(true);
    try {
      const r = await api.patch(
        `/workspaces/${wsId}/brands/${bid}/content-bucket/${row.id}`,
        { content: friendlyEdit });
      setRow(r.data);
      setEditMode(false);
      setFriendlyEdit(null);
      toast.success("Saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  // Iter 186 · Slice A — media upload / replace / delete
  const readAsDataURI = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res({ data_uri: r.result, filename: file.name,
                                mime: file.type,
                                kind: file.type.startsWith("video/") ? "video" : "image" });
    r.onerror = () => rej(new Error("read failed"));
    r.readAsDataURL(file);
  });

  const uploadNewMedia = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      for (const f of files) {
        const p = await readAsDataURI(f);
        const r = await api.post(
          `/workspaces/${wsId}/brands/${bid}/content-bucket/${row.id}/media`,
          { kind: p.kind, data_uri: p.data_uri, filename: p.filename });
        setRow(r.data);
      }
      toast.success(`Uploaded ${files.length} file(s)`);
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setBusy(false); }
  };

  const replaceMedia = async (index, kind, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const p = await readAsDataURI(file);
      const r = await api.put(
        `/workspaces/${wsId}/brands/${bid}/content-bucket/${row.id}/media/${index}`,
        { kind, data_uri: p.data_uri, filename: p.filename });
      setRow(r.data);
      toast.success("Replaced with your upload");
    } catch (e) { toast.error(apiErrorMessage(e, "Replace failed")); }
    finally { setBusy(false); }
  };

  const deleteMedia = async (index, kind) => {
    if (!window.confirm("Delete this media? This cannot be undone.")) return;
    setBusy(true);
    try {
      const r = await api.delete(
        `/workspaces/${wsId}/brands/${bid}/content-bucket/${row.id}/media/${index}`,
        { params: { kind } });
      setRow(r.data);
      toast.success("Deleted");
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
    finally { setBusy(false); }
  };

  const downloadMedia = async (index, kind, filename) => {
    try {
      // If data_uri is already loaded (from full get), use it; else fetch
      let dataUri;
      const arr = kind === "video" ? (row.videos || []) : (row.images || []);
      dataUri = arr[index]?.data_uri;
      if (!dataUri) {
        const r = await api.get(
          `/workspaces/${wsId}/brands/${bid}/content-bucket/${row.id}/media/${index}`,
          { params: { kind } });
        dataUri = r.data?.data_uri;
      }
      if (!dataUri) { toast.error("Nothing to download"); return; }
      const a = document.createElement("a");
      a.href = dataUri;
      a.download = filename || `${row.id}-${kind}-${index}.${dataUri.includes("png") ? "png" : dataUri.includes("jpeg") ? "jpg" : kind === "video" ? "mp4" : "bin"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) { toast.error("Download failed"); }
  };

  // Iter 189 · Bulk ZIP download for carousels
  const downloadAllZip = async () => {
    const images = row.images || [];
    const videos = row.videos || [];
    if (images.length + videos.length === 0) {
      toast.error("Nothing to download"); return;
    }
    setBusy(true);
    try {
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      const dataUriToBlob = (uri) => {
        const [meta, b64] = uri.split(",");
        const mime = /data:([^;]+)/.exec(meta)?.[1] || "application/octet-stream";
        const bin = atob(b64);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        return { blob: new Blob([arr], { type: mime }),
                    ext: mime.split("/")[1] || "bin" };
      };
      const fetchIfMissing = async (index, kind, existing) => {
        if (existing?.data_uri) return existing;
        const r = await api.get(
          `/workspaces/${wsId}/brands/${bid}/content-bucket/${row.id}/media/${index}`,
          { params: { kind } });
        return r.data || {};
      };
      let i = 0;
      for (const img of images) {
        const m = await fetchIfMissing(i, "image", img);
        if (m.data_uri) {
          const { blob, ext } = dataUriToBlob(m.data_uri);
          zip.file(m.filename || `image-${i + 1}.${ext}`, blob);
        }
        i += 1;
      }
      i = 0;
      for (const v of videos) {
        const m = await fetchIfMissing(i, "video", v);
        if (m.data_uri) {
          const { blob, ext } = dataUriToBlob(m.data_uri);
          zip.file(m.filename || `video-${i + 1}.${ext}`, blob);
        }
        i += 1;
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (row.topic_title || row.id).replace(/[^a-z0-9-]+/gi, "_").slice(0, 40);
      a.download = `${safe}-media.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${images.length + videos.length} file(s)`);
    } catch (e) { toast.error(apiErrorMessage(e, "ZIP download failed")); }
    finally { setBusy(false); }
  };


  const regenerate = async () => {
    if (!regenText.trim()) { toast.error("Feedback required"); return; }
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/brands/${bid}/content-bucket/${row.id}/regenerate`, { feedback: regenText });
      toast.success("Regeneration queued — result will update automatically");
      setRegenOpen(false);
      setRegenText("");
      reload();
    } catch (e) { toast.error(apiErrorMessage(e, "Regenerate failed")); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/workspaces/${wsId}/brands/${bid}/content-bucket/${row.id}`);
      toast.success("Deleted");
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const copyContent = () => {
    const txt = contentToText(row.content, row.format);
    navigator.clipboard.writeText(txt);
    toast.success("Copied as text");
  };

  const copyRichText = async () => {
    try {
      const html = contentToHtml(row.content, row.format);
      const plain = contentToText(row.content, row.format);
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([plain], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plain);
      }
      toast.success("Copied as rich text");
    } catch { toast.error("Rich copy not supported — copied as text"); }
  };

  const copyCaption = () => {
    const c = row.content || {};
    const body = c.body || c.caption || c.hook || c.title || "";
    const tags = (c.hashtags || []).map((h) => h.startsWith("#") ? h : `#${h}`).join(" ");
    const cta = c.cta ? `\n\n${c.cta}` : "";
    const out = `${body}${cta}${tags ? `\n\n${tags}` : ""}`.trim();
    if (!out) { toast.error("Nothing to copy"); return; }
    navigator.clipboard.writeText(out);
    toast.success("Copied caption + hashtags");
  };

  // Iter 184 · Approval workflow actions
  const callAction = async (path, body, successMsg) => {
    setBusy(true);
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brands/${bid}/content-bucket/${row.id}/${path}`,
        body || {});
      setRow(r.data);
      toast.success(successMsg);
    } catch (e) { toast.error(apiErrorMessage(e, "Action failed")); }
    finally { setBusy(false); }
  };

  const submitForReview = async (payload) => {
    setBusy(true);
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brands/${bid}/content-bucket/${row.id}/submit-review`,
        payload || {});
      setRow(r.data);
      toast.success("Submitted for review");
      setSubmitOpen(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Submit failed")); }
    finally { setBusy(false); }
  };

  const requestChanges = async () => {
    if (!changesNote.trim() || changesNote.trim().length < 3) {
      toast.error("Please describe the changes (min 3 chars)"); return;
    }
    await callAction("request-changes", { note: changesNote.trim() },
                            "Changes requested");
    setChangesOpen(false); setChangesNote("");
  };

  const saveSla = async (payload) => {
    setBusy(true);
    try {
      const r = await api.patch(
        `/workspaces/${wsId}/brands/${bid}/content-bucket/${row.id}/sla`,
        payload);
      setRow(r.data);
      toast.success("SLA updated");
      setSlaOpen(false);
    } catch (e) { toast.error(apiErrorMessage(e, "SLA update failed")); }
    finally { setBusy(false); }
  };

  const runDuplicate = async (tones) => {
    setBusy(true);
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brands/${bid}/content-bucket/${row.id}/duplicate-variants`,
        { tones, keep_images: false });
      toast.success(`Queued ${r.data.count} A/B tone variant(s)`);
      setAbOpen(false);
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Duplicate failed")); }
    finally { setBusy(false); }
  };

  const c = row?.content || {};
  const tone = STATUS_TONE[row?.status] || STATUS_TONE.draft;
  const sla = slaChip(row?.sla_due_at);
  const status = row?.status;
  const isDraftLike = ["draft", "queued", "changes_requested", "rejected", "error"].includes(status);
  const isInternalReview = status === "internal_review";
  const isClientReview = status === "client_review";
  const isReviewStage = isInternalReview || isClientReview;
  const canPushCalendar = ["approved", "internal_approved"].includes(status) && !row?.linked_content_item_id;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="content-bucket-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <TypeIcon format={row?.format} size={14} />
              <span className="text-xs font-mono uppercase text-muted-foreground">{row?.format}</span>
            </span>
            <span className={`text-[10px] uppercase font-bold border px-1 ${tone}`} data-testid="content-bucket-detail-status">{(row?.status || "").replace(/_/g, " ")}</span>
            {row?.linked_content_item && (
              <span className="inline-flex items-center gap-1 text-[10px] px-1 border"
                      style={{
                        color: row.linked_content_item.activity_type_color || "#059669",
                        borderColor: (row.linked_content_item.activity_type_color || "#059669") + "66",
                        background: (row.linked_content_item.activity_type_color || "#059669") + "10",
                      }}
                      title={`Package: ${row.linked_content_item.package_name || "—"}`}
                      data-testid="content-bucket-detail-deliverable">
                <TypeIcon iconKey={row.linked_content_item.activity_type_icon}
                                 format={row?.format}
                                 color={row.linked_content_item.activity_type_color} size={10} />
                <CalendarDays className="w-3 h-3" />
                <span>{row.linked_content_item.activity_type_name || row.linked_content_item.name || "Calendar"}</span>
                {row.linked_content_item.planned_date && (
                  <span className="opacity-70">· {row.linked_content_item.planned_date}</span>
                )}
              </span>
            )}
            {row?.client_visible && (
              <span className="text-[10px] uppercase font-bold border px-1 border-fuchsia-400 bg-fuchsia-50 text-fuchsia-800 inline-flex items-center gap-1"
                      data-testid="content-bucket-detail-client-visible">
                <Users className="w-3 h-3" /> client visible
              </span>
            )}
            {sla && (
              <span className={`text-[10px] uppercase font-bold border px-1 inline-flex items-center gap-1 ${sla.cls}`}
                      data-testid="content-bucket-detail-sla">
                <Clock className="w-3 h-3" /> {sla.label}
              </span>
            )}
            {row?.version > 1 && <Badge variant="outline" className="rounded-none text-[10px]">v{row.version}</Badge>}
            {row?.generating && <span className="text-[11px] text-primary inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> generating…</span>}
            <span className="ml-auto flex items-center gap-1 flex-wrap">
              <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={copyContent} data-testid="content-bucket-copy-btn" title="Copy as plain text">
                <ClipboardCopy className="w-3 h-3 mr-1" /> Copy
              </Button>
              <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={copyRichText}
                        data-testid="content-bucket-copy-rich-btn" title="Copy formatted (rich text)">
                <FileText className="w-3 h-3 mr-1" /> Rich
              </Button>
              <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={copyCaption}
                        data-testid="content-bucket-copy-caption-btn" title="Copy caption + hashtags (social-ready)">
                <Hash className="w-3 h-3 mr-1" /> Caption
              </Button>
              <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={() => setRegenOpen(true)} disabled={busy}
                        data-testid="content-bucket-regenerate-btn">
                <Wand2 className="w-3 h-3 mr-1" /> Regenerate
              </Button>
              <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={() => setAbOpen(true)}
                        disabled={busy || row?.generating}
                        data-testid="content-bucket-ab-btn">
                <CopyIcon className="w-3 h-3 mr-1" /> A/B tones
              </Button>
              <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={() => setSlaOpen(true)}
                        disabled={busy || row?.generating}
                        data-testid="content-bucket-sla-btn">
                <Clock className="w-3 h-3 mr-1" /> SLA
              </Button>
              <Button variant="outline" size="sm" className="rounded-none h-7 text-xs"
                        onClick={() => {
                          setEditMode(true);
                          setAdvancedJson(false);
                          setFriendlyEdit(JSON.parse(JSON.stringify(c || {})));
                          setEditText(JSON.stringify(c, null, 2));
                        }}
                        disabled={editMode || row?.generating} data-testid="content-bucket-edit-btn">
                <PenSquare className="w-3 h-3 mr-1" /> Edit
              </Button>

              {isDraftLike && !row?.generating && (
                <Button size="sm" className="rounded-none h-7 text-xs bg-violet-600 hover:bg-violet-700"
                          onClick={() => setSubmitOpen(true)} disabled={busy}
                          data-testid="content-bucket-submit-review-btn">
                  <Send className="w-3 h-3 mr-1" /> Submit for review
                </Button>
              )}

              {isInternalReview && (
                <Button size="sm" className="rounded-none h-7 text-xs bg-teal-600 hover:bg-teal-700"
                          onClick={() => callAction("approve-internal", {},
                            row?.client_visible ? "Approved — sent to client" : "Approved (internal)")}
                          disabled={busy}
                          data-testid="content-bucket-approve-internal-btn">
                  <UserCheck className="w-3 h-3 mr-1" /> Approve (internal)
                </Button>
              )}

              {isClientReview && (
                <Button size="sm" className="rounded-none h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => callAction("approve-client", {}, "Client approved ✓")}
                          disabled={busy}
                          data-testid="content-bucket-approve-client-btn">
                  <ShieldCheck className="w-3 h-3 mr-1" /> Approve (client)
                </Button>
              )}

              {isReviewStage && (
                <Button variant="outline" size="sm" className="rounded-none h-7 text-xs border-orange-400 text-orange-700 hover:bg-orange-50"
                          onClick={() => setChangesOpen(true)} disabled={busy}
                          data-testid="content-bucket-request-changes-btn">
                  <MessageSquareWarning className="w-3 h-3 mr-1" /> Request changes
                </Button>
              )}

              {canPushCalendar && (
                <Button size="sm" className="rounded-none h-7 text-xs bg-sky-600 hover:bg-sky-700"
                          onClick={() => onRequestLink && onRequestLink(row)}
                          data-testid="content-bucket-push-btn">
                  <Link2 className="w-3 h-3 mr-1" /> Push to Calendar
                </Button>
              )}

              {row?.linked_content_item_id && (
                <Button variant="outline" size="sm" className="rounded-none h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-50"
                          onClick={async () => {
                            if (!window.confirm("Unlink from calendar? The calendar row will remain but the connection is broken.")) return;
                            try {
                              await api.post(`/workspaces/${wsId}/brands/${bid}/content-bucket/${row.id}/unlink-calendar`);
                              toast.success("Unlinked");
                              reload();
                            } catch (e) { toast.error(apiErrorMessage(e, "Unlink failed")); }
                          }}
                          data-testid="content-bucket-unlink-btn">
                  <Link2Off className="w-3 h-3 mr-1" /> Unlink
                </Button>
              )}

              {status !== "rejected" && isReviewStage && (
                <Button variant="outline" size="sm" className="rounded-none h-7 text-xs border-rose-400 text-rose-700 hover:bg-rose-50"
                          onClick={() => callAction("reject-review", {}, "Rejected")}
                          disabled={busy || row?.generating}
                          data-testid="content-bucket-reject-btn">
                  <XCircle className="w-3 h-3 mr-1" /> Reject
                </Button>
              )}
              <button type="button" onClick={del} className="p-1 text-muted-foreground hover:text-rose-600"
                        data-testid="content-bucket-delete-btn">
                <Trash2 className="w-3 h-3" />
              </button>
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-auto space-y-3 pr-1">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Topic</div>
            <div className="text-sm font-medium">{row?.topic_title}</div>
            {row?.topic?.pillar && (
              <div className="text-[11px] text-muted-foreground">{row.topic.pillar} · {row.topic.funnel_stage} · {row.topic.target_persona}</div>
            )}
          </div>

          {row?.error && (
            <div className="border-2 border-rose-400 bg-rose-50 p-2 text-xs text-rose-900">
              <div className="font-semibold">Generation error</div>
              <div>{row.error}</div>
            </div>
          )}

          {editMode ? (
            advancedJson ? (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="label-eyebrow inline-block">Advanced · Content JSON</Label>
                  <Button variant="ghost" size="sm" className="rounded-none h-6 text-[10px]"
                            onClick={() => setAdvancedJson(false)}
                            data-testid="content-bucket-edit-friendly-toggle">
                    ← Back to friendly editor
                  </Button>
                </div>
                <Textarea rows={16} value={editText} onChange={(e) => setEditText(e.target.value)}
                            className="rounded-none font-mono text-xs" data-testid="content-bucket-edit-json" />
                <div className="flex justify-end gap-2 mt-2">
                  <Button variant="ghost" size="sm" className="rounded-none"
                            onClick={() => { setEditMode(false); setAdvancedJson(false); }}>Cancel</Button>
                  <Button size="sm" className="rounded-none" onClick={saveEdit} data-testid="content-bucket-edit-save-btn">Save JSON</Button>
                </div>
              </div>
            ) : (
              <FriendlyEditor value={friendlyEdit || c} format={row?.format}
                                    onChange={setFriendlyEdit}
                                    onCancel={() => { setEditMode(false); setFriendlyEdit(null); }}
                                    onSave={saveFriendly}
                                    onSwitchAdvanced={() => {
                                      setEditText(JSON.stringify(friendlyEdit || c, null, 2));
                                      setAdvancedJson(true);
                                    }}
                                    busy={busy} />
            )
          ) : (
            <>
              <ContentRenderer content={c} format={row?.format} images={row?.images || []} />
              <MediaManager row={row}
                                onUploadNew={uploadNewMedia}
                                onReplace={replaceMedia}
                                onDelete={deleteMedia}
                                onDownload={downloadMedia}
                                onDownloadZip={downloadAllZip}
                                busy={busy} />
              <StarRating wsId={wsId} bid={bid} cid={row.id} row={row}
                                 onChanged={reload} />
            </>
          )}

          {!editMode && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1 inline-flex items-center gap-1">
                <Activity className="w-3 h-3" /> Activity &amp; history
              </div>
              <ActivityFeed wsId={wsId} bid={bid} cid={row.id} />
            </div>
          )}

          {(row?.history || []).length > 0 && !editMode && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                History ({row.history.length})
              </div>
              <div className="space-y-1">
                {row.history.map((h, i) => (
                  <details key={i} className="border border-border p-2 bg-muted/20">
                    <summary className="text-xs cursor-pointer">
                      v{h.version} · {fmtRelative(h.at)} · {h.by_name} · <span className="italic text-muted-foreground">{h.regen_feedback?.slice(0, 60)}…</span>
                    </summary>
                    <pre className="mt-2 text-[10px] font-mono whitespace-pre-wrap max-h-40 overflow-auto">
{JSON.stringify(h.content, null, 2)}
                    </pre>
                  </details>
                ))}
              </div>
            </div>
          )}
        </div>

        {regenOpen && (
          <Dialog open onOpenChange={(v) => { if (!v) setRegenOpen(false); }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Wand2 className="w-4 h-4 text-primary" /> Regenerate with feedback
                </DialogTitle>
              </DialogHeader>
              <Textarea rows={5} value={regenText} onChange={(e) => setRegenText(e.target.value)}
                          placeholder="e.g. 'Make it shorter and add a stat about growth.'"
                          className="rounded-none" data-testid="content-bucket-regen-feedback" />
              <DialogFooter>
                <Button variant="ghost" className="rounded-none" onClick={() => setRegenOpen(false)}>Cancel</Button>
                <Button className="rounded-none" onClick={regenerate} disabled={busy}
                          data-testid="content-bucket-regen-submit-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  Regenerate
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {submitOpen && (
          <SubmitReviewDialog row={row} onClose={() => setSubmitOpen(false)}
                                      onSubmit={submitForReview} busy={busy} />
        )}
        {changesOpen && (
          <Dialog open onOpenChange={(v) => { if (!v) setChangesOpen(false); }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageSquareWarning className="w-4 h-4 text-orange-600" /> Request changes
                </DialogTitle>
              </DialogHeader>
              <Textarea rows={5} value={changesNote} onChange={(e) => setChangesNote(e.target.value)}
                          placeholder="What needs to change? (min 3 chars, required for audit trail)"
                          className="rounded-none" data-testid="content-bucket-changes-note" />
              <DialogFooter>
                <Button variant="ghost" className="rounded-none" onClick={() => setChangesOpen(false)}>Cancel</Button>
                <Button className="rounded-none bg-orange-600 hover:bg-orange-700" onClick={requestChanges} disabled={busy}
                          data-testid="content-bucket-changes-submit-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquareWarning className="w-4 h-4 mr-2" />}
                  Send back
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {slaOpen && (
          <SlaDialog row={row} onClose={() => setSlaOpen(false)}
                        onSave={saveSla} busy={busy} />
        )}
        {abOpen && (
          <AbVariantsDialog row={row} onClose={() => setAbOpen(false)}
                                onRun={runDuplicate} busy={busy} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ContentRenderer({ content, format, images }) {
  if (!content || Object.keys(content).length === 0) {
    return <div className="text-sm text-muted-foreground italic">Content will appear here once generation finishes.</div>;
  }
  return (
    <div className="space-y-3">
      {content.title && <h3 className="text-lg font-bold">{content.title}</h3>}
      {content.subtitle && <div className="text-sm text-muted-foreground">{content.subtitle}</div>}
      {content.hook && (
        <div className="border-l-4 border-primary bg-primary/5 p-2 text-sm">
          <span className="text-[9px] uppercase tracking-widest font-semibold text-muted-foreground">Hook</span>
          <div>{content.hook}</div>
        </div>
      )}
      {content.body && (
        <div className="text-sm whitespace-pre-wrap">{content.body}</div>
      )}
      {content.body_markdown && (
        <div className="text-sm whitespace-pre-wrap font-mono bg-muted/20 p-3 border border-border">{content.body_markdown}</div>
      )}
      {content.slides && (
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Slides ({content.slides.length})</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {content.slides.map((s, i) => {
              const img = images[i];
              return (
                <div key={i} className="border border-border bg-card p-2">
                  <div className="text-[9px] text-muted-foreground font-mono">Slide {i + 1}</div>
                  <div className="text-xs font-bold mt-0.5">{s.heading}</div>
                  <div className="text-[11px] mt-0.5">{s.body}</div>
                  {img?.data_uri && (
                    <img src={img.data_uri} alt="" className="w-full mt-2 border border-border" />
                  )}
                  {img?.error && (
                    <div className="text-[10px] text-rose-700 mt-1">Image: {img.error.slice(0, 80)}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {content.beats && (
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Beats</div>
          {content.beats.map((b, i) => (
            <div key={i} className="border border-border bg-card p-2">
              <div className="text-[10px] font-mono text-primary">{b.time}</div>
              <div className="text-xs"><strong>VO:</strong> {b.voiceover}</div>
              <div className="text-xs"><strong>On-screen:</strong> {b.on_screen}</div>
              {b.broll && <div className="text-[11px] text-muted-foreground">B-roll: {b.broll}</div>}
            </div>
          ))}
        </div>
      )}
      {content.headlines && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Headlines</div>
          <ul className="text-sm list-disc pl-5">
            {content.headlines.map((h, i) => <li key={i}>{h}</li>)}
          </ul>
        </div>
      )}
      {content.primary_texts && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Primary texts</div>
          <ul className="text-sm list-disc pl-5">
            {content.primary_texts.map((h, i) => <li key={i}>{h}</li>)}
          </ul>
        </div>
      )}
      {content.primary && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Primary keywords</div>
          <table className="w-full text-xs mt-1">
            <thead className="text-[9px] uppercase text-muted-foreground">
              <tr><th className="text-left px-1 py-0.5">Keyword</th><th className="text-left px-1 py-0.5">Intent</th><th className="text-left px-1 py-0.5">Diff.</th><th className="text-left px-1 py-0.5">Vol.</th></tr>
            </thead>
            <tbody>
              {content.primary.map((k, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-1 py-0.5 font-mono">{k.kw}</td><td className="px-1 py-0.5">{k.intent}</td>
                  <td className="px-1 py-0.5">{k.difficulty}</td><td className="px-1 py-0.5">{k.volume_hint}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {content.long_tail && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Long-tail</div>
          <div className="flex flex-wrap gap-1 mt-1">{content.long_tail.map((k, i) => <span key={i} className="text-[10px] font-mono border border-border bg-muted/40 px-1">{k}</span>)}</div>
        </div>
      )}
      {content.hashtags && (
        <div className="flex flex-wrap gap-1">
          {content.hashtags.map((h, i) => <span key={i} className="text-[10px] font-mono border border-border bg-muted/40 px-1">{h}</span>)}
        </div>
      )}
      {content.cta && (
        <div className="text-xs"><strong>CTA:</strong> {content.cta}</div>
      )}
      {format === "poster_image" && images?.[0]?.data_uri && (
        <img src={images[0].data_uri} alt="" className="w-full max-w-md border border-border" />
      )}
    </div>
  );
}

function ManualCreateDialog({ wsId, bid, formats, onClose, onCreated }) {
  const [form, setForm] = useState({
    topic_title: "", format: "linkedin_post", pillar: "",
    funnel_stage: "TOFU", body: "", cta: "", hashtags: "",
  });
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const readers = await Promise.all(files.map((f) => new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res({ data_uri: r.result, filename: f.name });
      r.readAsDataURL(f);
    })));
    setImages((prev) => [...prev, ...readers]);
  };

  const submit = async () => {
    if (!form.topic_title.trim()) { toast.error("Topic title required"); return; }
    setBusy(true);
    try {
      const content = {};
      if (form.body) content.body = form.body;
      if (form.cta) content.cta = form.cta;
      if (form.hashtags) content.hashtags = form.hashtags.split(/[\s,]+/).filter(Boolean);
      const r = await api.post(`/workspaces/${wsId}/brands/${bid}/content-bucket/manual`, {
        topic_title: form.topic_title, format: form.format,
        pillar: form.pillar || null, funnel_stage: form.funnel_stage || null,
        content, images,
      });
      toast.success(`Manual item created (${r.data.format})`);
      onCreated();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="content-bucket-manual-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenSquare className="w-4 h-4 text-primary" /> Add manual content
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <Label className="label-eyebrow mb-1 inline-block">Topic title *</Label>
              <Input className="h-9 rounded-none" value={form.topic_title}
                       onChange={(e) => setForm({ ...form, topic_title: e.target.value })}
                       placeholder="e.g. Cold brew launch teaser"
                       data-testid="content-bucket-manual-title" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Format</Label>
              <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
                <SelectTrigger className="h-9 rounded-none" data-testid="content-bucket-manual-format"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {formats.map((f) => <SelectItem key={f.key} value={f.key}>{f.key}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Funnel stage</Label>
              <Select value={form.funnel_stage} onValueChange={(v) => setForm({ ...form, funnel_stage: v })}>
                <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="TOFU">TOFU</SelectItem>
                  <SelectItem value="MOFU">MOFU</SelectItem>
                  <SelectItem value="BOFU">BOFU</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow mb-1 inline-block">Pillar</Label>
              <Input className="h-9 rounded-none" value={form.pillar}
                       onChange={(e) => setForm({ ...form, pillar: e.target.value })}
                       placeholder="e.g. Taste Education" />
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow mb-1 inline-block">Body / Copy</Label>
              <Textarea rows={5} value={form.body}
                          onChange={(e) => setForm({ ...form, body: e.target.value })}
                          className="rounded-none"
                          placeholder="Main copy for this post…"
                          data-testid="content-bucket-manual-body" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">CTA</Label>
              <Input className="h-9 rounded-none" value={form.cta}
                       onChange={(e) => setForm({ ...form, cta: e.target.value })}
                       placeholder="Shop now" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Hashtags (space/comma-sep)</Label>
              <Input className="h-9 rounded-none" value={form.hashtags}
                       onChange={(e) => setForm({ ...form, hashtags: e.target.value })}
                       placeholder="#coffee #brew #tips" />
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow mb-1 inline-block">Upload images / videos</Label>
              <label className="border-2 border-dashed border-border p-3 flex items-center gap-2 cursor-pointer hover:border-primary bg-muted/20" data-testid="content-bucket-manual-upload-label">
                <Upload className="w-4 h-4" />
                <span className="text-xs">Click to upload (any images or videos)</span>
                <input type="file" multiple accept="image/*,video/*" onChange={onFile} className="hidden"
                         data-testid="content-bucket-manual-upload" />
              </label>
              {images.length > 0 && (
                <div className="grid grid-cols-4 gap-1 mt-2">
                  {images.map((img, i) => (
                    <div key={i} className="border border-border p-1 relative">
                      {img.data_uri.startsWith("data:image") ? (
                        <img src={img.data_uri} alt="" className="w-full aspect-square object-cover" />
                      ) : (
                        <div className="aspect-square bg-muted flex items-center justify-center text-[10px]">{img.filename || "video"}</div>
                      )}
                      <button type="button" onClick={() => setImages(images.filter((_, j) => j !== i))}
                                className="absolute top-1 right-1 p-0.5 bg-white/90 border border-border">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy}
                    data-testid="content-bucket-manual-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PenSquare className="w-4 h-4 mr-2" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkCalendarDialog({ wsId, bid, bucket, onClose, onLinked }) {
  const [candidates, setCandidates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("existing"); // existing | adhoc
  const [selected, setSelected] = useState(null);
  const [pkgs, setPkgs] = useState([]);
  const [adhoc, setAdhoc] = useState({ package_id: "", planned_date: "", category: "adhoc" });

  useEffect(() => {
    api.get(`/workspaces/${wsId}/brands/${bid}/content-bucket/cal/candidates`)
      .then((r) => setCandidates(r.data.rows || []))
      .catch(() => {});
    api.get(`/workspaces/${wsId}/brands/${bid}/packages`)
      .then((r) => setPkgs(r.data || []))
      .catch(() => {});
  }, [wsId, bid]);

  const submit = async () => {
    setBusy(true);
    try {
      const body = mode === "existing"
        ? { content_item_id: selected }
        : { package_id: adhoc.package_id, planned_date: adhoc.planned_date, category: adhoc.category };
      await api.post(`/workspaces/${wsId}/brands/${bid}/content-bucket/${bucket.id}/link-calendar`, body);
      toast.success("Linked to calendar ✓");
      onLinked();
    } catch (e) { toast.error(apiErrorMessage(e, "Link failed")); }
    finally { setBusy(false); }
  };

  const canSubmit = mode === "existing" ? !!selected
    : (adhoc.package_id && adhoc.planned_date);

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="content-bucket-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-primary" /> Push to Calendar · {bucket.format}
          </DialogTitle>
        </DialogHeader>
        <div className="inline-flex border border-border mb-2">
          <button type="button" onClick={() => setMode("existing")}
                    className={`h-8 px-3 text-xs ${mode === "existing" ? "bg-primary text-primary-foreground" : "bg-card"}`}
                    data-testid="content-bucket-link-mode-existing">
            Attach to existing calendar item ({candidates.length})
          </button>
          <button type="button" onClick={() => setMode("adhoc")}
                    className={`h-8 px-3 text-xs border-l border-border ${mode === "adhoc" ? "bg-primary text-primary-foreground" : "bg-card"}`}
                    data-testid="content-bucket-link-mode-adhoc">
            Create ad-hoc calendar item
          </button>
        </div>
        {mode === "existing" ? (
          <div className="border border-border max-h-[380px] overflow-auto" data-testid="content-bucket-link-candidates">
            {candidates.length === 0 ? (
              <div className="text-xs text-muted-foreground italic p-4">
                No unlinked calendar items. Create packages with a schedule first, or switch to ad-hoc.
              </div>
            ) : candidates.map((c) => (
              <label key={c.id} className={`flex items-center gap-2 px-2 py-1.5 border-b border-border hover:bg-muted/30 cursor-pointer ${selected === c.id ? "bg-primary/10" : ""}`}
                       data-testid={`content-bucket-link-candidate-${c.id}`}>
                <input type="radio" checked={selected === c.id}
                         onChange={() => setSelected(c.id)} />
                <span className="text-xs font-mono w-24">{c.planned_date || "—"}</span>
                <span className="text-xs font-medium flex-1 truncate">{c.topic || c.category}</span>
                <span className="text-[10px] text-muted-foreground">{c.package_name} · {c.category}</span>
              </label>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <Label className="label-eyebrow mb-1 inline-block">Package *</Label>
              <Select value={adhoc.package_id} onValueChange={(v) => setAdhoc({ ...adhoc, package_id: v })}>
                <SelectTrigger className="h-9 rounded-none" data-testid="content-bucket-link-adhoc-package"><SelectValue placeholder="Select a package" /></SelectTrigger>
                <SelectContent>
                  {pkgs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Planned date *</Label>
              <Input type="date" className="h-9 rounded-none" value={adhoc.planned_date}
                       onChange={(e) => setAdhoc({ ...adhoc, planned_date: e.target.value })}
                       data-testid="content-bucket-link-adhoc-date" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Category</Label>
              <Input className="h-9 rounded-none" value={adhoc.category}
                       onChange={(e) => setAdhoc({ ...adhoc, category: e.target.value })} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={!canSubmit || busy}
                    data-testid="content-bucket-link-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
            Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Iter 184 · Phase 4 sub-dialogs — Submit for review · SLA · A/B tones
// ────────────────────────────────────────────────────────────────────────

function SubmitReviewDialog({ row, onClose, onSubmit, busy }) {
  const [clientVisible, setClientVisible] = useState(!!row?.client_visible);
  const [slaDate, setSlaDate] = useState(row?.sla_due_at ? row.sla_due_at.slice(0, 16) : "");
  const [note, setNote] = useState("");

  const submit = () => {
    const payload = { client_visible: clientVisible };
    if (slaDate) {
      try { payload.sla_due_at = new Date(slaDate).toISOString(); }
      catch { payload.sla_due_at = slaDate; }
    }
    if (note.trim()) payload.note = note.trim();
    onSubmit(payload);
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="content-bucket-submit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-4 h-4 text-violet-600" /> Submit for review
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer border border-border p-2"
                    data-testid="content-bucket-submit-client-visible-toggle">
            <input type="checkbox" checked={clientVisible}
                     onChange={(e) => setClientVisible(e.target.checked)} />
            <div className="flex-1">
              <div className="text-xs font-semibold flex items-center gap-1"><Users className="w-3 h-3" /> Client visible</div>
              <div className="text-[11px] text-muted-foreground">If ON, adds a second client-review step after internal approval.</div>
            </div>
          </label>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">SLA due (optional)</Label>
            <Input type="datetime-local" className="h-9 rounded-none" value={slaDate}
                     onChange={(e) => setSlaDate(e.target.value)}
                     data-testid="content-bucket-submit-sla-input" />
            <div className="text-[10px] text-muted-foreground mt-1">Reviewers get an email when this is within 24h and again if breached.</div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Note (optional)</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                        placeholder="Context for reviewers…"
                        className="rounded-none" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none bg-violet-600 hover:bg-violet-700" onClick={submit} disabled={busy}
                    data-testid="content-bucket-submit-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SlaDialog({ row, onClose, onSave, busy }) {
  const [clientVisible, setClientVisible] = useState(!!row?.client_visible);
  const [slaDate, setSlaDate] = useState(row?.sla_due_at ? row.sla_due_at.slice(0, 16) : "");

  const save = () => {
    const payload = { client_visible: clientVisible };
    if (slaDate) {
      try { payload.sla_due_at = new Date(slaDate).toISOString(); }
      catch { payload.sla_due_at = slaDate; }
    } else {
      payload.sla_due_at = null;
    }
    onSave(payload);
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="content-bucket-sla-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" /> SLA &amp; visibility
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow mb-1 inline-block">SLA due date &amp; time</Label>
            <Input type="datetime-local" className="h-9 rounded-none" value={slaDate}
                     onChange={(e) => setSlaDate(e.target.value)}
                     data-testid="content-bucket-sla-input" />
            <div className="text-[10px] text-muted-foreground mt-1">Leave blank to clear the SLA.</div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer border border-border p-2"
                    data-testid="content-bucket-sla-client-toggle">
            <input type="checkbox" checked={clientVisible}
                     onChange={(e) => setClientVisible(e.target.checked)} />
            <div className="flex-1">
              <div className="text-xs font-semibold flex items-center gap-1"><Users className="w-3 h-3" /> Client visible</div>
              <div className="text-[11px] text-muted-foreground">Adds a client-review step to the workflow.</div>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy}
                    data-testid="content-bucket-sla-save-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const _DEFAULT_TONES = [
  { key: "professional",   label: "Professional",   desc: "Polished, corporate, authoritative." },
  { key: "witty",          label: "Witty",          desc: "Playful hooks, dry humour, wordplay." },
  { key: "bold",           label: "Bold",           desc: "Provocative, high-energy, punchy." },
  { key: "conversational", label: "Conversational", desc: "Warm, casual, second person." },
  { key: "data_driven",    label: "Data-driven",    desc: "Numbers-first, quantified claims." },
  { key: "empathetic",     label: "Empathetic",     desc: "Human-first, acknowledges pain points." },
];

function AbVariantsDialog({ row, onClose, onRun, busy }) {
  const [sel, setSel] = useState(new Set(["professional", "witty", "bold"]));
  const toggle = (k) => {
    const s = new Set(sel);
    if (s.has(k)) s.delete(k); else s.add(k);
    setSel(s);
  };
  const submit = () => {
    if (sel.size === 0) { toast.error("Pick at least one tone"); return; }
    onRun(Array.from(sel));
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="content-bucket-ab-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CopyIcon className="w-4 h-4 text-primary" /> Duplicate for A/B — tone variants
          </DialogTitle>
        </DialogHeader>
        <div className="text-[11px] text-muted-foreground mb-2">
          Generates a fresh copy of this item for each selected tone. Topic, format, and CTA stay the same — only the voice changes.
        </div>
        <div className="grid grid-cols-1 gap-1 border border-border">
          {_DEFAULT_TONES.map((t) => {
            const on = sel.has(t.key);
            return (
              <button key={t.key} type="button" onClick={() => toggle(t.key)}
                        className={`flex items-start gap-2 px-2 py-1.5 text-left border-b border-border last:border-b-0 hover:bg-muted/30 ${on ? "bg-primary/10" : ""}`}
                        data-testid={`content-bucket-ab-tone-${t.key}`}>
                <span className={`mt-0.5 w-3 h-3 border ${on ? "bg-primary border-primary" : "border-border"}`} />
                <div className="flex-1">
                  <div className="text-xs font-semibold">{t.label}</div>
                  <div className="text-[10px] text-muted-foreground">{t.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="text-[10px] text-muted-foreground mt-2">
          Will queue <strong>{sel.size}</strong> variant(s). Max 6.
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy || sel.size === 0}
                    data-testid="content-bucket-ab-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CopyIcon className="w-4 h-4 mr-2" />}
            Duplicate {sel.size}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function TimelineView({ wsId, bid }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (from) p.set("from_date", from);
    if (to) p.set("to_date", to);
    api.get(`/workspaces/${wsId}/brands/${bid}/content-bucket/tl/summary?${p.toString()}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Failed")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [wsId, bid]);

  const rows = data?.rows || [];
  // Group by month
  const byMonth = {};
  rows.forEach((r) => {
    const m = r.month || (r.planned_date || "").slice(0, 7) || "unknown";
    (byMonth[m] = byMonth[m] || []).push(r);
  });
  const months = Object.keys(byMonth).sort().reverse();
  const statusTone = (s) => ({
    published: "bg-emerald-500", planned: "bg-sky-500", in_progress: "bg-amber-500",
  }[s] || "bg-muted-foreground/50");

  return (
    <div className="space-y-4" data-testid="content-bucket-timeline">
      <div className="border border-border bg-card p-2 flex items-center gap-2 flex-wrap">
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">From</Label>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                 className="h-8 w-36 rounded-none text-xs" data-testid="content-bucket-timeline-from" />
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">To</Label>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                 className="h-8 w-36 rounded-none text-xs" data-testid="content-bucket-timeline-to" />
        <Button variant="outline" size="sm" className="rounded-none h-8" onClick={load}
                  data-testid="content-bucket-timeline-apply-btn">Apply</Button>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <KpiCard label="Items pushed" value={data.total || 0} />
          <KpiCard label="Published" value={data.by_status.published || 0} tone="text-emerald-700" />
          <KpiCard label="Planned" value={data.by_status.planned || 0} tone="text-sky-700" />
          <KpiCard label="In progress" value={data.by_status.in_progress || 0} tone="text-amber-700" />
          <KpiCard label="Cancelled" value={data.by_status.cancelled || 0} tone="text-rose-700" />
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground p-6 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground italic">
          No items pushed to calendar yet. Approve a bucket item and click <strong>Push to Calendar</strong>.
        </div>
      ) : (
        <div className="space-y-3">
          {months.map((m) => (
            <div key={m} className="border border-border bg-card">
              <div className="border-b border-border bg-muted/30 px-3 py-1.5 flex items-center justify-between">
                <span className="text-sm font-bold">{m}</span>
                <Badge variant="outline" className="rounded-none text-[10px]">{byMonth[m].length} items</Badge>
              </div>
              <div className="divide-y divide-border">
                {byMonth[m].map((r) => (
                  <div key={r.bucket_id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/20"
                         data-testid={`content-bucket-timeline-row-${r.bucket_id}`}>
                    <span className={`w-1.5 h-4 ${statusTone(r.calendar_status)}`} />
                    <span className="text-xs font-mono w-24">{r.planned_date || "—"}</span>
                    <span className="text-xs font-mono w-28 truncate text-muted-foreground">{r.format}</span>
                    <span className="text-xs flex-1 truncate">{r.topic}</span>
                    <span className="text-[10px] border border-border px-1 uppercase">{r.calendar_status}</span>
                    {r.published_platform && (
                      <span className="text-[10px] border border-emerald-300 bg-emerald-50 text-emerald-800 px-1">{r.published_platform}</span>
                    )}
                    {r.published_url && (
                      <a href={r.published_url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-[10px]"
                           data-testid={`content-bucket-timeline-link-${r.bucket_id}`}>view →</a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, tone = "" }) {
  return (
    <div className="border border-border bg-card p-2">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className={`text-xl font-mono font-black mt-0.5 ${tone}`}>{value}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Iter 185 · Slice B — List view (sortable columns · bulk select)
// ────────────────────────────────────────────────────────────────────────

function SortHeader({ label, k, sortKey, sortDir, onSort, className = "" }) {
  const active = sortKey === k;
  const Icon = !active ? ChevronsUpDown : sortDir === "asc" ? ChevronUp : ChevronDown;
  return (
    <button type="button" onClick={() => onSort(k)}
              className={`inline-flex items-center gap-1 uppercase tracking-widest text-[9px] font-bold hover:text-primary ${active ? "text-primary" : "text-muted-foreground"} ${className}`}
              data-testid={`content-bucket-list-sort-${k}`}>
      {label} <Icon className="w-3 h-3" />
    </button>
  );
}

function ListView({ rows, selected, onToggle, onToggleAll, onOpen,
                          sortKey, sortDir, onSort }) {
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someChecked = rows.some((r) => selected.has(r.id));
  return (
    <div className="border border-border bg-card overflow-x-auto" data-testid="content-bucket-list-view">
      <table className="w-full text-xs" style={{ minWidth: 1100 }}>
        <thead className="bg-muted/40 border-b border-border">
          <tr>
            <th className="px-2 py-2 text-left w-8">
              <input type="checkbox" checked={allChecked}
                       ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                       onChange={(e) => onToggleAll(e.target.checked)}
                       data-testid="content-bucket-list-check-all" />
            </th>
            <th className="px-2 py-2 text-left"><SortHeader label="Topic" k="topic" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></th>
            <th className="px-2 py-2 text-left"><SortHeader label="Format" k="format" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></th>
            <th className="px-2 py-2 text-left uppercase tracking-widest text-[9px] font-bold text-muted-foreground">Deliverable</th>
            <th className="px-2 py-2 text-left"><SortHeader label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></th>
            <th className="px-2 py-2 text-left uppercase tracking-widest text-[9px] font-bold text-muted-foreground">Vis.</th>
            <th className="px-2 py-2 text-left"><SortHeader label="SLA" k="sla" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></th>
            <th className="px-2 py-2 text-left"><SortHeader label="Author" k="author" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></th>
            <th className="px-2 py-2 text-left"><SortHeader label="Last edit" k="last_edited" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></th>
            <th className="px-2 py-2 text-left"><SortHeader label="Cycle" k="cycle" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></th>
            <th className="px-2 py-2 text-left"><SortHeader label="Ver" k="version" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></th>
            <th className="px-2 py-2 text-left uppercase tracking-widest text-[9px] font-bold text-muted-foreground">Activity</th>
            <th className="px-2 py-2 text-right uppercase tracking-widest text-[9px] font-bold text-muted-foreground">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const tone = STATUS_TONE[r.status] || STATUS_TONE.draft;
            const sla = slaChip(r.sla_due_at);
            const cycle = fmtDuration(r.timings?.total_cycle_seconds);
            const checked = selected.has(r.id);
            return (
              <tr key={r.id} className={`border-b border-border hover:bg-muted/30 ${checked ? "bg-primary/5" : ""}`}
                     data-testid={`content-bucket-list-row-${r.id}`}>
                <td className="px-2 py-1.5">
                  <input type="checkbox" checked={checked}
                           onChange={() => onToggle(r.id)}
                           data-testid={`content-bucket-list-check-${r.id}`} />
                </td>
                <td className="px-2 py-1.5">
                  <button type="button" onClick={() => onOpen(r)}
                            className="text-left font-medium hover:text-primary underline-offset-2 hover:underline max-w-xs truncate block"
                            data-testid={`content-bucket-list-open-${r.id}`}>
                    {r.content?.title || r.topic_title || r.id.slice(0, 8)}
                  </button>
                </td>
                <td className="px-2 py-1.5 font-mono text-[10px] uppercase text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <TypeIcon format={r.format} size={11} />
                    <span>{r.format}</span>
                  </span>
                </td>
                <td className="px-2 py-1.5 text-[11px]">
                  {r.linked_content_item ? (
                    <div className="min-w-0"
                            title={`${r.linked_content_item.activity_type_name || r.linked_content_item.name || "—"} · ${r.linked_content_item.planned_date || ""}${r.linked_content_item.package_name ? " · " + r.linked_content_item.package_name : ""}`}>
                      <div className="inline-flex items-center gap-1 truncate max-w-[16ch]"
                              style={{ color: r.linked_content_item.activity_type_color || "#059669" }}>
                        <TypeIcon iconKey={r.linked_content_item.activity_type_icon}
                                         format={r.format}
                                         color={r.linked_content_item.activity_type_color}
                                         size={11} />
                        <span className="font-medium truncate">{r.linked_content_item.activity_type_name || r.linked_content_item.name || "—"}</span>
                      </div>
                      {r.linked_content_item.planned_date && (
                        <div className="text-[9px] text-muted-foreground">{r.linked_content_item.planned_date}</div>
                      )}
                    </div>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-2 py-1.5">
                  <span className={`border px-1 text-[9px] uppercase font-bold ${tone}`}>{(r.status || "").replace(/_/g, " ")}</span>
                </td>
                <td className="px-2 py-1.5">
                  {r.client_visible && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] border border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800 px-1">
                      <Users className="w-2.5 h-2.5" /> client
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5">
                  {sla ? (
                    <span className={`inline-flex items-center gap-0.5 text-[10px] border px-1 ${sla.cls}`}>
                      {sla.label}
                    </span>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-2 py-1.5 text-[11px]">
                  <div className="flex items-center gap-1">
                    <UserIcon className="w-3 h-3 text-muted-foreground" />
                    <span className="truncate max-w-[10ch]">{r.created_by_name || "—"}</span>
                  </div>
                  {r.generation_source === "ab_variant" && (
                    <div className="text-[9px] text-indigo-700 uppercase">A/B · {r.ab_variant_tone}</div>
                  )}
                  {r.generation_source === "manual" && (
                    <div className="text-[9px] text-violet-700 uppercase">manual</div>
                  )}
                </td>
                <td className="px-2 py-1.5 text-[11px]">
                  {r.last_edited_by_name ? (
                    <div>
                      <div className="truncate max-w-[10ch]">{r.last_edited_by_name}</div>
                      <div className="text-[9px] text-muted-foreground">{fmtRelative(r.last_edited_at || r.updated_at)}</div>
                    </div>
                  ) : (
                    <div className="text-[9px] text-muted-foreground">{fmtRelative(r.updated_at)}</div>
                  )}
                </td>
                <td className="px-2 py-1.5 text-[11px] font-mono">
                  {cycle || <span className="text-muted-foreground">—</span>}
                  {r.timings?.sla_met === true && <div className="text-[9px] text-emerald-700 uppercase">SLA met</div>}
                  {r.timings?.sla_met === false && <div className="text-[9px] text-rose-700 uppercase">SLA missed</div>}
                </td>
                <td className="px-2 py-1.5 text-[11px]">v{r.version || 1}</td>
                <td className="px-2 py-1.5 text-[11px]">
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <Activity className="w-3 h-3" /> {r.activity_count || 0}
                  </span>
                  {r.image_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-muted-foreground ml-1">
                      <ImageIcon className="w-3 h-3" /> {r.image_count}
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <Button size="sm" variant="outline" className="rounded-none h-6 text-[10px] px-2"
                            onClick={() => onOpen(r)}
                            data-testid={`content-bucket-list-open-btn-${r.id}`}>
                    Open
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Iter 185 · Slice B — Activity feed (used in Detail dialog)
// ────────────────────────────────────────────────────────────────────────

function ActivityFeed({ wsId, bid, cid }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(
          `/workspaces/${wsId}/brands/${bid}/content-bucket/${cid}/activity`);
        if (alive) setData(r.data);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [wsId, bid, cid]);

  if (loading) return <div className="text-xs text-muted-foreground py-4 text-center">Loading activity…</div>;
  if (!data || data.events.length === 0) {
    return <div className="text-xs text-muted-foreground italic py-4 text-center">No activity recorded yet.</div>;
  }

  const t = data.timings || {};
  return (
    <div data-testid="content-bucket-activity-feed">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <StatChip label="Total cycle" value={fmtDuration(t.total_cycle_seconds) || "—"} tone="primary" testid="ct-cycle" />
        <StatChip label="Time to approval" value={fmtDuration(t.time_to_approval_seconds) || "—"} tone="emerald" testid="ct-approval" />
        <StatChip label="Time to publish" value={fmtDuration(t.time_to_publish_seconds) || "—"} tone="sky" testid="ct-publish" />
        <StatChip label="SLA outcome"
                       value={t.sla_met === true ? "MET" : t.sla_met === false ? "MISSED" : "—"}
                       tone={t.sla_met === true ? "emerald" : t.sla_met === false ? "rose" : "muted"}
                       testid="ct-sla" />
      </div>
      {t.stage_seconds && Object.keys(t.stage_seconds).length > 0 && (
        <div className="flex items-center gap-1 flex-wrap mb-3" data-testid="content-bucket-stage-times">
          {Object.entries(t.stage_seconds).map(([stage, secs]) => (
            <span key={stage} className="inline-flex items-center gap-1 text-[10px] border border-border bg-card px-1.5 py-0.5">
              <span className="text-muted-foreground uppercase tracking-widest">{stage.replace(/_/g, " ")}</span>
              <span className="font-mono font-bold">{fmtDuration(secs) || "0s"}</span>
            </span>
          ))}
        </div>
      )}
      <div className="space-y-1">
        {data.events.map((e, i) => (
          <ActivityEvent key={i} evt={e} />
        ))}
      </div>
    </div>
  );
}

function ActivityEvent({ evt }) {
  const iconMap = {
    created: <Sparkles className="w-3 h-3 text-primary" />,
    approval: <ShieldCheck className="w-3 h-3 text-emerald-600" />,
    edit: <PenSquare className="w-3 h-3 text-violet-600" />,
    content_edit: <PenSquare className="w-3 h-3 text-violet-600" />,
    status_change: <Send className="w-3 h-3 text-sky-600" />,
    regenerate: <Wand2 className="w-3 h-3 text-primary" />,
    media: <ImageIcon className="w-3 h-3 text-orange-600" />,
  };
  const icon = iconMap[evt.type] || <Activity className="w-3 h-3 text-muted-foreground" />;
  const label = describeActivity(evt);
  return (
    <div className="border border-border bg-card p-2 text-xs flex items-start gap-2"
           data-testid={`content-bucket-activity-event-${evt.type}`}>
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="font-medium">{label}</div>
        {evt.details?.field_changes && (
          <div className="mt-1 space-y-0.5">
            {evt.details.field_changes.slice(0, 5).map((f, j) => (
              <div key={j} className="text-[10px] border-l-2 border-violet-300 pl-2">
                <span className="font-mono uppercase text-violet-700">{f.field}</span>
                <span className="text-muted-foreground line-through mx-1">{f.before || "∅"}</span>
                <span>→</span>
                <span className="ml-1 font-medium">{f.after || "∅"}</span>
              </div>
            ))}
            {evt.details.field_changes.length > 5 && (
              <div className="text-[10px] text-muted-foreground">+{evt.details.field_changes.length - 5} more field(s)</div>
            )}
          </div>
        )}
        {evt.details?.note && (
          <div className="text-[11px] text-muted-foreground italic mt-0.5">&quot;{evt.details.note}&quot;</div>
        )}
        {evt.details?.regen_feedback && (
          <div className="text-[11px] text-muted-foreground italic mt-0.5">&quot;{evt.details.regen_feedback}&quot;</div>
        )}
        <div className="text-[10px] text-muted-foreground mt-0.5">
          {evt.by_name || "—"} · {fmtRelative(evt.at)}
        </div>
      </div>
    </div>
  );
}

function describeActivity(e) {
  const d = e.details || {};
  if (e.type === "created") {
    return d.source === "manual" ? "Created manually" :
                d.source === "ab_variant" ? `Created as A/B variant (${d.ab_variant_tone})` :
                "AI generated";
  }
  if (e.type === "approval") {
    return {
      submit: "Submitted for review",
      approve_internal: "Approved (internal)",
      approve_internal_visible: "Approved · sent to client review",
      approve_client: "Client approved",
      promote_to_client: "Promoted to client review",
      request_changes: "Requested changes",
      reject: "Rejected",
    }[d.action] || d.action || "Approval action";
  }
  if (e.type === "content_edit" || e.type === "edit") return "Content edited";
  if (e.type === "status_change") return `Status: ${d.from || "—"} → ${d.to || "—"}`;
  if (e.type === "regenerate") return `Regenerated (v${(d.version_snapshot || 0) + 1})`;
  if (e.type === "media") return `Media ${d.op || "changed"}`;
  return e.type;
}

function StatChip({ label, value, tone = "primary", testid }) {
  const map = {
    primary: "border-primary/40 bg-primary/5 text-primary",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-900",
    sky: "border-sky-300 bg-sky-50 text-sky-900",
    rose: "border-rose-300 bg-rose-50 text-rose-900",
    muted: "border-border bg-card text-muted-foreground",
  };
  return (
    <div className={`border p-2 ${map[tone] || map.muted}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest font-semibold opacity-80">{label}</div>
      <div className="text-sm font-mono font-black mt-0.5">{value}</div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// Iter 186 · Slice A — content → text / rich text converters
// ═══════════════════════════════════════════════════════════════════════

const _esc = (s) => (s == null ? "" : String(s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"));

function contentToText(c, format) {
  if (!c || typeof c !== "object") return "";
  const parts = [];
  if (c.title) parts.push(c.title);
  if (c.subtitle) parts.push(c.subtitle);
  if (c.hook) parts.push(c.hook);
  if (c.body) parts.push(c.body);
  if (c.body_markdown) parts.push(c.body_markdown);
  if (c.caption) parts.push(c.caption);
  if (Array.isArray(c.slides)) {
    c.slides.forEach((s, i) => {
      parts.push(`\n--- Slide ${i + 1} ---`);
      if (s.heading) parts.push(s.heading);
      if (s.body) parts.push(s.body);
    });
  }
  if (Array.isArray(c.beats)) {
    c.beats.forEach((b) => {
      parts.push(`[${b.time || ""}] VO: ${b.voiceover || ""}`);
      if (b.on_screen) parts.push(`  On-screen: ${b.on_screen}`);
      if (b.broll) parts.push(`  B-roll: ${b.broll}`);
    });
  }
  if (Array.isArray(c.headlines)) {
    parts.push("\nHeadlines:");
    c.headlines.forEach((h) => parts.push(`\u2022 ${h}`));
  }
  if (Array.isArray(c.primary_texts)) {
    parts.push("\nPrimary texts:");
    c.primary_texts.forEach((t) => parts.push(`\u2022 ${t}`));
  }
  if (Array.isArray(c.descriptions)) {
    parts.push("\nDescriptions:");
    c.descriptions.forEach((t) => parts.push(`\u2022 ${t}`));
  }
  if (Array.isArray(c.primary)) {
    parts.push("\nKeywords:");
    c.primary.forEach((k) => parts.push(`\u2022 ${k.kw || k}`));
  }
  if (Array.isArray(c.long_tail) && c.long_tail.length) {
    parts.push("\nLong-tail:");
    parts.push(c.long_tail.join(", "));
  }
  if (c.meta_title) parts.push(`\nMeta title: ${c.meta_title}`);
  if (c.meta_description) parts.push(`Meta description: ${c.meta_description}`);
  if (c.cta) parts.push(`\n${c.cta}`);
  if (Array.isArray(c.hashtags) && c.hashtags.length) {
    const tags = c.hashtags.map((h) => h.startsWith("#") ? h : `#${h}`).join(" ");
    parts.push(`\n${tags}`);
  }
  return parts.join("\n").trim();
}

function contentToHtml(c, format) {
  if (!c || typeof c !== "object") return "";
  const p = [];
  if (c.title) p.push(`<h2>${_esc(c.title)}</h2>`);
  if (c.subtitle) p.push(`<h3 style="color:#666">${_esc(c.subtitle)}</h3>`);
  if (c.hook) p.push(`<blockquote>${_esc(c.hook)}</blockquote>`);
  if (c.body) p.push(`<div>${_esc(c.body).replace(/\n/g, "<br>")}</div>`);
  if (c.body_markdown) p.push(`<pre>${_esc(c.body_markdown)}</pre>`);
  if (c.caption) p.push(`<p>${_esc(c.caption).replace(/\n/g, "<br>")}</p>`);
  if (Array.isArray(c.slides)) {
    c.slides.forEach((s, i) => {
      p.push(`<div style="margin:12px 0;padding:8px;border:1px solid #ddd"><div style="font-size:10px;color:#999">SLIDE ${i + 1}</div>${s.heading ? `<h4>${_esc(s.heading)}</h4>` : ""}${s.body ? `<div>${_esc(s.body).replace(/\n/g, "<br>")}</div>` : ""}</div>`);
    });
  }
  if (Array.isArray(c.beats)) {
    p.push("<ol>");
    c.beats.forEach((b) => {
      p.push(`<li><strong>${_esc(b.time)}</strong> \u2014 ${_esc(b.voiceover)}</li>`);
    });
    p.push("</ol>");
  }
  if (Array.isArray(c.headlines)) {
    p.push("<strong>Headlines</strong><ul>");
    c.headlines.forEach((h) => p.push(`<li>${_esc(h)}</li>`));
    p.push("</ul>");
  }
  if (c.cta) p.push(`<p><strong>${_esc(c.cta)}</strong></p>`);
  if (Array.isArray(c.hashtags) && c.hashtags.length) {
    const tags = c.hashtags.map((h) => h.startsWith("#") ? h : `#${h}`).join(" ");
    p.push(`<p style="color:#0369a1">${_esc(tags)}</p>`);
  }
  return `<div style="font-family:system-ui,sans-serif">${p.join("")}</div>`;
}

// ═══════════════════════════════════════════════════════════════════════
// Iter 186 · Slice A — MediaManager (below rendered content, non-edit mode)
// ═══════════════════════════════════════════════════════════════════════

function MediaManager({ row, onUploadNew, onReplace, onDelete, onDownload,
                                onDownloadZip, busy }) {
  const images = row?.images || [];
  const videos = row?.videos || [];
  const [expanded, setExpanded] = useState(true);
  const uploadRef = useRef(null);
  const replaceRef = useRef({});

  const anyMedia = images.length + videos.length > 0;
  const showZip = anyMedia && (images.length + videos.length) >= 2;

  const triggerReplace = (index, kind) => {
    const el = replaceRef.current[`${kind}-${index}`];
    if (el) el.click();
  };

  return (
    <div className="border border-border bg-card p-2" data-testid="content-bucket-media-manager">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold inline-flex items-center gap-1">
          <ImageIcon className="w-3 h-3" /> Media ({images.length} image{images.length !== 1 ? "s" : ""}
          {videos.length > 0 && `, ${videos.length} video${videos.length !== 1 ? "s" : ""}`})
        </div>
        <div className="flex items-center gap-1">
          <input ref={uploadRef} type="file" accept="image/*,video/*" multiple hidden
                   onChange={(e) => { onUploadNew(Array.from(e.target.files || [])); e.target.value = ""; }}
                   data-testid="content-bucket-media-upload-input" />
          <Button size="sm" variant="outline" className="rounded-none h-6 text-[10px]"
                    onClick={() => uploadRef.current?.click()} disabled={busy}
                    data-testid="content-bucket-media-upload-btn">
            <Upload className="w-3 h-3 mr-1" /> Upload
          </Button>
          {showZip && (
            <Button size="sm" variant="outline" className="rounded-none h-6 text-[10px]"
                      onClick={onDownloadZip} disabled={busy}
                      data-testid="content-bucket-media-zip-btn">
              <Download className="w-3 h-3 mr-1" /> Download ZIP
            </Button>
          )}
          <Button size="sm" variant="ghost" className="rounded-none h-6 text-[10px]"
                    onClick={() => setExpanded(!expanded)}>
            {expanded ? "Collapse" : "Expand"}
          </Button>
        </div>
      </div>
      {expanded && (
        <>
          {!anyMedia && (
            <div className="text-[11px] text-muted-foreground italic mt-2 text-center py-3 border border-dashed border-border">
              No media yet. Click <strong>Upload</strong> to add your own image or video.
            </div>
          )}
          {images.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2" data-testid="content-bucket-media-images">
              {images.map((img, i) => (
                <MediaTile key={`img-${i}`} media={img} index={i} kind="image"
                                onReplace={(f) => onReplace(i, "image", f)}
                                onDelete={() => onDelete(i, "image")}
                                onDownload={() => onDownload(i, "image", img.filename)}
                                triggerReplace={() => triggerReplace(i, "image")}
                                replaceRef={(el) => (replaceRef.current[`image-${i}`] = el)} />
              ))}
            </div>
          )}
          {videos.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2" data-testid="content-bucket-media-videos">
              {videos.map((v, i) => (
                <MediaTile key={`vid-${i}`} media={v} index={i} kind="video"
                                onReplace={(f) => onReplace(i, "video", f)}
                                onDelete={() => onDelete(i, "video")}
                                onDownload={() => onDownload(i, "video", v.filename)}
                                triggerReplace={() => triggerReplace(i, "video")}
                                replaceRef={(el) => (replaceRef.current[`video-${i}`] = el)} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MediaTile({ media, index, kind, onReplace, onDelete, onDownload,
                            triggerReplace, replaceRef }) {
  const isUpload = media?.source === "user_upload";
  const label = isUpload ? "Your upload" : "AI generated";
  return (
    <div className="relative border border-border bg-white group"
           data-testid={`content-bucket-media-tile-${kind}-${index}`}>
      {kind === "image" ? (
        media?.data_uri ? (
          <img src={media.data_uri} alt="" className="w-full aspect-square object-cover" />
        ) : (
          <div className="w-full aspect-square bg-muted flex items-center justify-center text-[10px] text-muted-foreground">No preview</div>
        )
      ) : (
        media?.data_uri ? (
          <video src={media.data_uri} controls className="w-full aspect-video bg-black" />
        ) : (
          <div className="w-full aspect-video bg-muted flex items-center justify-center text-[10px] text-muted-foreground">No preview</div>
        )
      )}
      <div className="absolute top-1 left-1 flex items-center gap-1">
        <span className={`text-[9px] font-bold uppercase border px-1 ${isUpload ? "border-violet-400 bg-violet-50 text-violet-800" : "border-primary/40 bg-white text-primary"}`}>
          {isUpload ? <><UserIcon className="w-2.5 h-2.5 inline mr-0.5" /> {label}</> : <><Bot className="w-2.5 h-2.5 inline mr-0.5" /> {label}</>}
        </span>
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] p-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
        <input ref={replaceRef} type="file" hidden
                 accept={kind === "video" ? "video/*" : "image/*"}
                 onChange={(e) => { if (e.target.files?.[0]) onReplace(e.target.files[0]); e.target.value = ""; }}
                 data-testid={`content-bucket-media-replace-input-${kind}-${index}`} />
        <button type="button" onClick={triggerReplace}
                  className="hover:text-primary inline-flex items-center gap-0.5"
                  title="Replace with your file"
                  data-testid={`content-bucket-media-replace-btn-${kind}-${index}`}>
          <RefreshCcw className="w-3 h-3" /> Replace
        </button>
        <button type="button" onClick={onDownload}
                  className="hover:text-primary inline-flex items-center gap-0.5"
                  title="Download"
                  data-testid={`content-bucket-media-download-btn-${kind}-${index}`}>
          <Download className="w-3 h-3" /> Save
        </button>
        <button type="button" onClick={onDelete}
                  className="ml-auto hover:text-rose-400 inline-flex items-center gap-0.5"
                  title="Delete"
                  data-testid={`content-bucket-media-delete-btn-${kind}-${index}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      {media?.filename && (
        <div className="text-[9px] text-muted-foreground truncate px-1 py-0.5">{media.filename}</div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Iter 186 · Slice A — FriendlyEditor
// ═══════════════════════════════════════════════════════════════════════

function FriendlyEditor({ value, format, onChange, onCancel, onSave,
                                  onSwitchAdvanced, busy }) {
  const [local, setLocal] = useState(() => value || {});
  useEffect(() => { if (onChange) onChange(local); }, [local, onChange]);

  const upd = (k, v) => setLocal((s) => ({ ...s, [k]: v }));

  const kind = ({
    blog_post: "long_text",
    linkedin_post: "short_text",
    x_post: "micro",
    instagram_caption: "short_text",
    whatsapp_broadcast: "short_text",
    email_newsletter: "long_text",
    instagram_carousel: "carousel",
    instagram_reel_script: "script",
    short_video_script: "script",
    youtube_script: "script",
    ad_copy_meta: "ad_copy",
    ad_copy_google: "ad_copy",
    seo_keywords: "keywords",
    seo_meta: "meta",
    poster_image: "poster",
  })[format] || "generic";

  const parseHashtags = (s) =>
    s.split(/[\s,]+/).map((t) => t.trim()).filter(Boolean);

  const hashtagsStr = (Array.isArray(local.hashtags)
    ? local.hashtags.map((h) => h.startsWith("#") ? h : `#${h}`).join(" ")
    : (local.hashtags || ""));

  return (
    <div className="border border-primary/40 bg-primary/5 p-3 space-y-3"
           data-testid="content-bucket-friendly-editor">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold text-primary inline-flex items-center gap-1">
          <PenSquare className="w-3 h-3" /> Editing — {format?.replace(/_/g, " ")}
        </div>
        <button type="button" onClick={onSwitchAdvanced}
                  className="text-[10px] text-muted-foreground hover:text-primary underline underline-offset-2"
                  data-testid="content-bucket-friendly-advanced-toggle">
          Advanced (JSON) →
        </button>
      </div>

      {["short_text", "long_text", "micro"].includes(kind) && (
        <>
          <FieldText label="Title" value={local.title} onChange={(v) => upd("title", v)}
                        data-testid="content-bucket-fe-title" />
          <FieldText label="Hook (optional)" value={local.hook} onChange={(v) => upd("hook", v)}
                        data-testid="content-bucket-fe-hook" />
          <FieldTextarea label="Body" value={local.body || ""} rows={8}
                              onChange={(v) => upd("body", v)}
                              data-testid="content-bucket-fe-body" />
          <FieldText label="CTA" value={local.cta} onChange={(v) => upd("cta", v)}
                        data-testid="content-bucket-fe-cta" />
          <FieldText label="Hashtags (space or comma-separated)"
                        value={hashtagsStr}
                        onChange={(v) => upd("hashtags", parseHashtags(v))}
                        data-testid="content-bucket-fe-hashtags" />
        </>
      )}

      {kind === "carousel" && (
        <>
          <FieldText label="Title / hook" value={local.title || local.hook || ""}
                        onChange={(v) => upd("title", v)}
                        data-testid="content-bucket-fe-title" />
          <FieldTextarea label="Caption" value={local.caption || local.body || ""} rows={5}
                              onChange={(v) => upd("caption", v)}
                              data-testid="content-bucket-fe-caption" />
          <div className="space-y-1">
            <Label className="label-eyebrow inline-block">Slides ({(local.slides || []).length})</Label>
            {(local.slides || []).map((s, i) => (
              <div key={i} className="border border-border bg-white p-2 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="text-[9px] font-mono uppercase text-muted-foreground">Slide {i + 1}</div>
                  <button type="button" onClick={() => {
                    const next = (local.slides || []).filter((_, j) => j !== i);
                    upd("slides", next);
                  }} className="text-rose-600 hover:text-rose-700"
                             data-testid={`content-bucket-fe-slide-del-${i}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                <FieldText label="Headline" value={s.heading || ""}
                              onChange={(v) => {
                                const next = [...(local.slides || [])];
                                next[i] = { ...s, heading: v };
                                upd("slides", next);
                              }} />
                <FieldTextarea label="Body" value={s.body || ""} rows={3}
                                    onChange={(v) => {
                                      const next = [...(local.slides || [])];
                                      next[i] = { ...s, body: v };
                                      upd("slides", next);
                                    }} />
              </div>
            ))}
            <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                      onClick={() => upd("slides", [...(local.slides || []),
                                                                { heading: "", body: "" }])}
                      data-testid="content-bucket-fe-slide-add">
              + Add slide
            </Button>
          </div>
          <FieldText label="CTA" value={local.cta} onChange={(v) => upd("cta", v)} />
          <FieldText label="Hashtags" value={hashtagsStr}
                        onChange={(v) => upd("hashtags", parseHashtags(v))} />
        </>
      )}

      {kind === "script" && (
        <>
          <FieldText label="Title" value={local.title || ""} onChange={(v) => upd("title", v)}
                        data-testid="content-bucket-fe-title" />
          <FieldText label="Hook" value={local.hook || ""} onChange={(v) => upd("hook", v)} />
          <div className="space-y-1">
            <Label className="label-eyebrow inline-block">Beats ({(local.beats || []).length})</Label>
            {(local.beats || []).map((b, i) => (
              <div key={i} className="border border-border bg-white p-2 grid grid-cols-1 md:grid-cols-4 gap-1">
                <FieldText label="Time" value={b.time || ""}
                              onChange={(v) => {
                                const next = [...(local.beats || [])];
                                next[i] = { ...b, time: v };
                                upd("beats", next);
                              }} />
                <div className="md:col-span-2">
                  <FieldTextarea label="Voiceover" rows={2} value={b.voiceover || ""}
                                      onChange={(v) => {
                                        const next = [...(local.beats || [])];
                                        next[i] = { ...b, voiceover: v };
                                        upd("beats", next);
                                      }} />
                </div>
                <FieldTextarea label="On-screen / B-roll" rows={2}
                                    value={b.on_screen || b.broll || ""}
                                    onChange={(v) => {
                                      const next = [...(local.beats || [])];
                                      next[i] = { ...b, on_screen: v };
                                      upd("beats", next);
                                    }} />
                <button type="button" onClick={() => {
                  const next = (local.beats || []).filter((_, j) => j !== i);
                  upd("beats", next);
                }} className="md:col-span-4 text-rose-600 hover:text-rose-700 text-[10px] text-right">
                  <Trash2 className="w-3 h-3 inline" /> Remove beat
                </button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                      onClick={() => upd("beats", [...(local.beats || []),
                                                                { time: "", voiceover: "", on_screen: "" }])}
                      data-testid="content-bucket-fe-beat-add">
              + Add beat
            </Button>
          </div>
          <FieldText label="CTA" value={local.cta} onChange={(v) => upd("cta", v)} />
        </>
      )}

      {kind === "ad_copy" && (
        <>
          <FieldChipList label="Headlines" value={local.headlines || []}
                                onChange={(v) => upd("headlines", v)}
                                data-testid="content-bucket-fe-headlines" />
          <FieldChipList label={format === "ad_copy_google" ? "Descriptions" : "Primary texts"}
                                value={local.descriptions || local.primary_texts || []}
                                onChange={(v) => upd(format === "ad_copy_google" ? "descriptions" : "primary_texts", v)}
                                data-testid="content-bucket-fe-primary-texts" />
          <FieldText label="Display URL / CTA" value={local.cta || local.display_url || ""}
                        onChange={(v) => upd("cta", v)} />
        </>
      )}

      {kind === "meta" && (
        <>
          <FieldText label="Meta title (≤ 60 chars)" value={local.meta_title || ""}
                        onChange={(v) => upd("meta_title", v)}
                        data-testid="content-bucket-fe-meta-title" />
          <FieldTextarea label="Meta description (≤ 160 chars)"
                              rows={3}
                              value={local.meta_description || ""}
                              onChange={(v) => upd("meta_description", v)}
                              data-testid="content-bucket-fe-meta-description" />
          <FieldChipList label="Focus keywords" value={local.keywords || []}
                                onChange={(v) => upd("keywords", v)} />
        </>
      )}

      {kind === "keywords" && (
        <>
          <div className="text-[11px] text-muted-foreground">Editing detailed keyword tables requires the Advanced editor. Switch above for full control.</div>
          <FieldChipList label="Long-tail keywords" value={local.long_tail || []}
                                onChange={(v) => upd("long_tail", v)} />
        </>
      )}

      {kind === "poster" && (
        <>
          <FieldText label="Title" value={local.title} onChange={(v) => upd("title", v)} />
          <FieldText label="Subtitle" value={local.subtitle} onChange={(v) => upd("subtitle", v)} />
          <FieldText label="CTA" value={local.cta} onChange={(v) => upd("cta", v)} />
          <div className="text-[11px] text-muted-foreground italic">The image itself can be replaced from the Media section (once you save).</div>
        </>
      )}

      {kind === "generic" && (
        <div className="text-xs text-muted-foreground italic">No friendly editor for this format yet — use Advanced JSON.</div>
      )}

      <div className="flex justify-end gap-2 pt-1 border-t border-border">
        <Button variant="ghost" size="sm" className="rounded-none" onClick={onCancel}
                  data-testid="content-bucket-friendly-cancel">Cancel</Button>
        <Button size="sm" className="rounded-none" onClick={onSave} disabled={busy}
                  data-testid="content-bucket-friendly-save-btn">
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
          Save
        </Button>
      </div>
    </div>
  );
}

function FieldText({ label, value, onChange, ...rest }) {
  return (
    <div>
      <Label className="label-eyebrow mb-1 inline-block">{label}</Label>
      <Input className="h-8 rounded-none text-xs" value={value || ""}
               onChange={(e) => onChange(e.target.value)}
               {...rest} />
    </div>
  );
}
function FieldTextarea({ label, value, onChange, rows = 4, ...rest }) {
  return (
    <div>
      <Label className="label-eyebrow mb-1 inline-block">{label}</Label>
      <Textarea rows={rows} className="rounded-none text-xs" value={value || ""}
                   onChange={(e) => onChange(e.target.value)}
                   {...rest} />
    </div>
  );
}
function FieldChipList({ label, value = [], onChange, ...rest }) {
  const [draft, setDraft] = useState("");
  const remove = (i) => onChange(value.filter((_, j) => j !== i));
  const add = () => {
    const s = draft.trim();
    if (s) onChange([...value, s]);
    setDraft("");
  };
  return (
    <div>
      <Label className="label-eyebrow mb-1 inline-block">{label} ({value.length})</Label>
      <div className="flex items-center gap-1 flex-wrap mb-1" {...rest}>
        {value.map((v, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-[10px] border border-border bg-card px-1.5 py-0.5">
            <span className="truncate max-w-[16ch]">{typeof v === "string" ? v : (v.kw || JSON.stringify(v))}</span>
            <button type="button" onClick={() => remove(i)} className="hover:text-rose-600">
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-1">
        <Input className="h-7 rounded-none text-xs flex-1" value={draft}
                 onChange={(e) => setDraft(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                 placeholder="Type and press Enter…" />
        <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                  onClick={add}>Add</Button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// Iter 188 · Slice C — StarRating widget
// ═══════════════════════════════════════════════════════════════════════

export function StarRating({ wsId, bid, cid, row, onChanged, size = 16 }) {
  const [busy, setBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [review, setReview] = useState("");
  const [hover, setHover] = useState(0);

  const ratings = row?.ratings || [];
  const my = ratings.find((r) => r.by && r.by_name); // best-effort local match; server enforces per-user
  const myStars = my?.stars;
  const avg = row?.rating_avg;
  const count = row?.rating_count || 0;

  const submit = async (stars, reviewText) => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/brands/${bid}/content-bucket/${cid}/rate`,
                          { stars, review: reviewText || null });
      toast.success(`Rated ${stars}★`);
      onChanged && onChanged();
      setReviewOpen(false);
      setReview("");
    } catch (e) { toast.error(apiErrorMessage(e, "Rating failed")); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.delete(`/workspaces/${wsId}/brands/${bid}/content-bucket/${cid}/rate`);
      toast.success("Rating removed");
      onChanged && onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, "Remove failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-card p-2 space-y-1" data-testid="content-bucket-star-rating">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Rate this content</div>
        {avg != null && (
          <div className="text-[11px] text-muted-foreground">
            <span className="font-bold text-amber-700">{avg.toFixed(2)}</span>
            <span className="opacity-60"> avg · {count} rating{count === 1 ? "" : "s"}</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-0.5" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = (hover || myStars || 0) >= n;
          return (
            <button key={n} type="button" disabled={busy}
                      onClick={() => submit(n, null)}
                      onMouseEnter={() => setHover(n)}
                      className="hover:scale-110 transition-transform"
                      title={`${n} star${n === 1 ? "" : "s"}`}
                      data-testid={`content-bucket-star-${n}`}>
              <Star size={size} className={filled ? "fill-amber-500 text-amber-500" : "text-muted-foreground"} />
            </button>
          );
        })}
        <div className="ml-2 flex items-center gap-1">
          <Button size="sm" variant="ghost" className="rounded-none h-6 text-[10px]"
                    onClick={() => setReviewOpen(!reviewOpen)}
                    data-testid="content-bucket-review-toggle">
            {reviewOpen ? "− review" : "+ review"}
          </Button>
          {myStars && (
            <Button size="sm" variant="ghost" className="rounded-none h-6 text-[10px] text-rose-700"
                      onClick={clear} disabled={busy}
                      data-testid="content-bucket-clear-rating">
              Clear
            </Button>
          )}
        </div>
      </div>
      {reviewOpen && (
        <div className="space-y-1">
          <Textarea rows={2} value={review} onChange={(e) => setReview(e.target.value)}
                       placeholder="Optional review — what worked, what didn't…"
                       className="rounded-none text-xs"
                       data-testid="content-bucket-review-text" />
          <div className="flex justify-end gap-1">
            {[3, 4, 5].map((n) => (
              <Button key={n} size="sm" variant="outline"
                        className="rounded-none h-6 text-[10px]"
                        disabled={busy}
                        onClick={() => submit(n, review)}
                        data-testid={`content-bucket-review-submit-${n}`}>
                Submit {n}★
              </Button>
            ))}
          </div>
        </div>
      )}
      {ratings.length > 0 && (
        <div className="space-y-0.5 pt-1 border-t border-border">
          {ratings.slice(0, 5).map((r, i) => (
            <div key={i} className="text-[10px] flex items-center gap-1">
              <span className="inline-flex text-amber-600">
                {"★".repeat(r.stars || 0)}<span className="text-muted-foreground">{"★".repeat(5 - (r.stars || 0))}</span>
              </span>
              <span className="font-medium truncate max-w-[10ch]">{r.by_name || "—"}</span>
              {r.review && <span className="text-muted-foreground italic truncate flex-1">&quot;{r.review}&quot;</span>}
            </div>
          ))}
          {ratings.length > 5 && (
            <div className="text-[10px] text-muted-foreground">+{ratings.length - 5} more</div>
          )}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// Iter 188 · Slice C — Team Leaderboard Dialog
// ═══════════════════════════════════════════════════════════════════════

function LeaderboardDialog({ wsId, bid, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [window_, setWindow] = useState(90);
  const [scopeBrand, setScopeBrand] = useState(true);

  const load = () => {
    setLoading(true);
    const params = { window_days: window_ };
    if (scopeBrand && bid) params.brand_id = bid;
    api.get(`/workspaces/${wsId}/content-bucket/leaderboard`, { params })
        .then((r) => setData(r.data))
        .catch(() => setData(null))
        .finally(() => setLoading(false));
  };
  useEffect(load, [wsId, bid, window_, scopeBrand]);

  const renderRow = (c, metricKey, metricLabel) => (
    <tr key={c.user_id} className="border-b border-border">
      <td className="px-2 py-1.5 font-medium truncate">{c.user_name || "—"}</td>
      <td className="px-2 py-1.5 text-right font-mono">{c[metricKey] ?? "—"}</td>
      <td className="px-2 py-1.5 text-right text-[10px] text-muted-foreground">{c.items_shipped}</td>
      <td className="px-2 py-1.5 text-right text-[10px] text-muted-foreground">
        {c.avg_rating ? `${c.avg_rating}★` : "—"}
      </td>
      <td className="px-2 py-1.5 text-right text-[10px] text-muted-foreground">
        {c.avg_cycle_hours != null ? `${c.avg_cycle_hours}h` : "—"}
      </td>
    </tr>
  );

  const sections = data ? [
    { title: "🏆 Velocity leaderboard", subtitle: "items_shipped × rating ÷ cycle_days", key: "velocity_score",
      rows: data.top_velocity, unit: "" },
    { title: "⭐ Top rated", subtitle: "Average star rating", key: "avg_rating",
      rows: data.top_rating, unit: "★" },
    { title: "🚚 Most shipped", subtitle: "Items past draft state", key: "items_shipped",
      rows: data.top_shipped, unit: "" },
    { title: "🤖 AI-vs-manual", subtitle: "Higher = more AI-generated", key: "ai_manual_ratio",
      rows: data.top_ai_ratio, unit: "" },
    { title: "⏱ SLA-met %", subtitle: "% of items delivered on time", key: "sla_met_pct",
      rows: data.top_sla_pct, unit: "%" },
  ] : [];

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="content-bucket-leaderboard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
            Content team leaderboard
            <span className="ml-auto flex items-center gap-1">
              {[30, 90, 180].map((w) => (
                <button key={w} type="button"
                          onClick={() => setWindow(w)}
                          className={`h-6 px-2 text-[10px] border ${window_ === w ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}
                          data-testid={`content-bucket-lb-window-${w}`}>
                  {w}d
                </button>
              ))}
              <button type="button"
                        onClick={() => setScopeBrand(!scopeBrand)}
                        className={`h-6 px-2 text-[10px] border ${scopeBrand ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}
                        data-testid="content-bucket-lb-scope-brand">
                {scopeBrand ? "This brand" : "All brands"}
              </button>
            </span>
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-muted-foreground p-8 text-center">Loading leaderboard…</div>
        ) : !data || data.creators.length === 0 ? (
          <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground italic">
            No content creators found in this window.
          </div>
        ) : (
          <>
            {/* Iter 189 · AI vs Human benchmark strip */}
            {data.ai_vs_human && <AiVsHumanBenchmark data={data.ai_vs_human} />}
            {/* Iter 191 · Format-level quality gap */}
            {data.quality_by_format && <QualityByFormatChart data={data.quality_by_format} />}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sections.map((s) => (
                <div key={s.key} className="border border-border bg-card p-2"
                        data-testid={`content-bucket-lb-section-${s.key}`}>
                  <div className="text-[11px] uppercase tracking-widest font-bold text-primary">{s.title}</div>
                  <div className="text-[10px] text-muted-foreground mb-1">{s.subtitle}</div>
                  {s.rows.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground italic py-2 text-center">No data yet.</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="text-[9px] uppercase tracking-widest text-muted-foreground">
                        <tr>
                          <th className="px-2 py-1 text-left">Creator</th>
                          <th className="px-2 py-1 text-right">{s.title.split(" ")[0]}</th>
                          <th className="px-2 py-1 text-right">Ship</th>
                          <th className="px-2 py-1 text-right">Avg★</th>
                          <th className="px-2 py-1 text-right">Cycle</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.rows.slice(0, 10).map((c) => renderRow(c, s.key, s.title))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Iter 189 — AI vs Human quality benchmark
function AiVsHumanBenchmark({ data }) {
  const ws = data.workspace || {};
  const buckets = [
    { key: "ai", label: "AI-generated", color: "#0ea5e9", accent: "bg-sky-500" },
    { key: "manual", label: "Manual (human)", color: "#a855f7", accent: "bg-violet-500" },
    { key: "ab_variant", label: "A/B tone variants", color: "#c026d3", accent: "bg-fuchsia-500" },
  ];
  const maxRating = 5;
  const maxItems = Math.max(1, ...buckets.map((b) => ws[b.key]?.items || 0));
  const delta = data.delta_ai_minus_manual;
  const gapTone = delta == null ? "text-muted-foreground"
    : Math.abs(delta) < 0.3 ? "text-emerald-700"
    : delta > 0 ? "text-sky-700" : "text-violet-700";
  const gapLabel = delta == null ? "no data"
    : Math.abs(delta) < 0.3 ? "AI matches human quality"
    : delta > 0 ? `AI ${delta > 0 ? "+" : ""}${delta.toFixed(2)}★ over manual`
    : `Manual ${(-delta).toFixed(2)}★ over AI`;

  return (
    <div className="border-2 border-primary/40 bg-primary/5 p-3 mb-3" data-testid="content-bucket-ai-vs-human">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-primary">🤖 vs 👤 · Quality benchmark</div>
          <div className="text-[10px] text-muted-foreground">Compare avg ratings of AI vs human-created content in this window</div>
        </div>
        <div className={`text-xs font-bold ${gapTone}`} data-testid="content-bucket-ai-vs-human-delta">
          {gapLabel}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {buckets.map((b) => {
          const stat = ws[b.key] || {};
          const rating = stat.avg_rating;
          const items = stat.items || 0;
          const shipped = stat.shipped || 0;
          const barPct = rating ? (rating / maxRating) * 100 : 0;
          const volPct = maxItems > 0 ? (items / maxItems) * 100 : 0;
          return (
            <div key={b.key} className="border border-border bg-card p-2"
                     data-testid={`content-bucket-ai-vs-human-${b.key}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: b.color }}>{b.label}</span>
                <span className="text-[10px] font-mono text-muted-foreground">{items} · {shipped} shipped</span>
              </div>
              {/* Rating bar */}
              <div className="text-[10px] text-muted-foreground">Avg rating</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-muted border border-border">
                  <div className="h-full" style={{ width: `${barPct}%`, background: b.color }} />
                </div>
                <span className="text-xs font-mono font-bold w-12 text-right">
                  {rating != null ? `${rating.toFixed(2)}★` : "—"}
                </span>
              </div>
              {/* Volume bar */}
              <div className="text-[10px] text-muted-foreground mt-1">Volume</div>
              <div className="h-1.5 bg-muted border border-border">
                <div className={`h-full ${b.accent} opacity-60`} style={{ width: `${volPct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      {data.per_creator && data.per_creator.length > 0 && (
        <div className="mt-2 pt-2 border-t border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Per-creator mix (top by volume)</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
            {data.per_creator.slice(0, 9).map((c) => {
              const total = c.ai_items + c.manual_items + c.ab_items;
              const aiPct = total ? (c.ai_items / total) * 100 : 0;
              const manPct = total ? (c.manual_items / total) * 100 : 0;
              const abPct = total ? (c.ab_items / total) * 100 : 0;
              return (
                <div key={c.user_id} className="border border-border bg-card p-1.5"
                        data-testid={`content-bucket-ai-vs-human-creator-${c.user_id}`}>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="font-medium truncate">{c.user_name || "—"}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">
                      {c.avg_rating ? `${c.avg_rating}★` : "—"}
                    </span>
                  </div>
                  <div className="h-1.5 flex mt-0.5 border border-border">
                    <div className="bg-sky-500" style={{ width: `${aiPct}%` }} title={`AI ${c.ai_items}`} />
                    <div className="bg-violet-500" style={{ width: `${manPct}%` }} title={`Manual ${c.manual_items}`} />
                    <div className="bg-fuchsia-500" style={{ width: `${abPct}%` }} title={`A/B ${c.ab_items}`} />
                  </div>
                  <div className="text-[9px] font-mono text-muted-foreground mt-0.5">
                    {c.ai_items} AI · {c.manual_items} manual · {c.ab_items} A/B
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Iter 191 · Format-level Quality Gap chart
// ═══════════════════════════════════════════════════════════════════════

function QualityByFormatChart({ data }) {
  const rows = (data.rows || []).slice(0, 12);
  if (rows.length === 0) return null;

  const buckets = [
    { key: "ai", label: "AI", color: "#0ea5e9" },
    { key: "manual", label: "Manual", color: "#a855f7" },
    { key: "ab_variant", label: "A/B", color: "#c026d3" },
  ];
  const toneRing = {
    emerald: "border-emerald-500 text-emerald-800 bg-emerald-50",
    sky: "border-sky-500 text-sky-800 bg-sky-50",
    amber: "border-amber-500 text-amber-900 bg-amber-50",
    muted: "border-border text-muted-foreground bg-muted/30",
  };

  return (
    <div className="border-2 border-primary/40 bg-card p-3 mb-3"
              data-testid="content-bucket-quality-by-format">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-primary">
            📊 Quality gap by format
          </div>
          <div className="text-[10px] text-muted-foreground">
            Per-format AI-vs-Human quality benchmark · same 0.3★ threshold
          </div>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
          {buckets.map((b) => (
            <span key={b.key} className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2" style={{ background: b.color }} />
              {b.label}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-1.5">
        {rows.map((r) => (
          <div key={r.format} className="border border-border bg-white p-2 grid grid-cols-[130px_1fr_180px] gap-2 items-center"
                    data-testid={`content-bucket-qbf-row-${r.format}`}>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-widest truncate">
                {r.format}
              </div>
              <div className="text-[10px] text-muted-foreground font-mono">
                {r.total} items
              </div>
            </div>
            <div className="space-y-0.5">
              {buckets.map((b) => {
                const s = r[b.key] || {};
                const rating = s.avg_rating;
                const items = s.items || 0;
                const barPct = rating ? (rating / 5) * 100 : 0;
                return (
                  <div key={b.key} className="flex items-center gap-1.5"
                            data-testid={`content-bucket-qbf-bar-${r.format}-${b.key}`}>
                    <span className="text-[9px] w-9 font-semibold uppercase tracking-widest text-muted-foreground">{b.label}</span>
                    <div className="flex-1 h-2 bg-muted border border-border relative">
                      <div className="h-full" style={{ width: `${barPct}%`, background: b.color }} />
                    </div>
                    <span className="text-[10px] font-mono w-16 text-right">
                      {rating != null ? `${rating.toFixed(2)}★` : "—"} · {items}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className={`text-[10px] font-semibold px-1.5 py-0.5 border ${toneRing[r.insight?.tone] || toneRing.muted} text-center`}
                     data-testid={`content-bucket-qbf-insight-${r.format}`}>
              {r.insight?.label || "—"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

